<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <div class="sidebar-header">
        <button class="toggle-btn" @click="toggleSidebar">
          <span v-if="!isCollapsed">☰</span>
          <span v-else>→</span>
        </button>
        <h1 v-if="!isCollapsed" class="sidebar-title">Property Management</h1>
      </div>
      
      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item">
          <span class="nav-icon">🏠</span>
          <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
        </router-link>
        <router-link to="/reservations" class="nav-item" active-class="active">
          <span class="nav-icon">📋</span>
          <span v-if="!isCollapsed" class="nav-text">Reservations</span>
        </router-link>
        <router-link to="/properties" class="nav-item">
          <span class="nav-icon">🏨</span>
          <span v-if="!isCollapsed" class="nav-text">Properties</span>
        </router-link>
        <a @click="openContactsModal" class="nav-item" style="cursor: pointer;">
          <span class="nav-icon">📧</span>
          <span v-if="!isCollapsed" class="nav-text">Email Contacts</span>
        </a>
        <router-link to="/settings" class="nav-item">
          <span class="nav-icon">⚙️</span>
          <span v-if="!isCollapsed" class="nav-text">Settings</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-header">
        <h1>Reservations</h1>
        <div class="user-info">
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <!-- This Month Section -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>This Month - {{ getCurrentMonth() }}</h2>
            <div class="property-selector">
              <label for="currentProperty">Property:</label>
              <select 
                id="currentProperty"
                v-model="selectedCurrentProperty" 
                @change="onCurrentPropertyChange"
                class="property-select-small"
                :disabled="loading"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading current month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedCurrentMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(unitName, 'current')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadRapportPropertyExcel(unitName, 'current')" class="btn btn-sm btn-rapport-excel">
                  Rapport Excel
                </button>
                <button @click="openReportModal(unitName, 'current')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment }">
                <td class="guest-name">
                  {{ reservation.guestName }}
                <span v-if="reservation.isSegment && reservation.segmentInfo.total > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
  SPLIT {{ reservation.segmentInfo.part }}/{{ reservation.segmentInfo.total }}
</span>
                </td>
                                  <td>
                      <span class="source-badge">{{ reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedCurrentMonth).length === 0" class="empty-state">
            <p>No reservations found for this month</p>
          </div>
        </div>
      </div>

      <!-- Last Month Section -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>Last Month - {{ getLastMonth() }}</h2>
            <div class="property-selector">
              <label for="lastMonthProperty">Property:</label>
              <select 
                id="lastMonthProperty"
                v-model="selectedLastMonthProperty" 
                @change="onLastMonthPropertyChange"
                class="property-select-small"
                :disabled="loadingLastMonth"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loadingLastMonth" class="loading-state">
          <div class="spinner"></div>
          <p>Loading last month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedLastMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>
              <div class="property-actions">
                <button @click="downloadRapportPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-rapport-excel">
                  Rapport Excel
                </button>
                <button @click="downloadPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="openReportModal(unitName, 'lastmonth')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment }">
  <td class="guest-name">
    {{ reservation.guestName }}
    <span v-if="reservation.isSegment" class="segment-badge" :title="getSegmentTooltip(reservation)">
      SPLIT {{ reservation.segmentInfo.part }}/{{ reservation.segmentInfo.total }}
    </span>
  </td>
                    <td>
                      <span class="source-badge">{{ reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedLastMonth).length === 0" class="empty-state">
            <p>No reservations found for last month</p>
          </div>
        </div>
      </div>

      <!-- History Section -->
      <div class="section-card">
        <div class="section-header">
          <h2>Reservations History</h2>
        </div>
        
        <div class="filters-section">
          <div class="filter-group">
            <label>Property:</label>
            <select v-model="selectedProperty" class="filter-select">
              <option value="">All Properties</option>
              <option v-for="property in properties" :key="property.id" :value="property.id">
                {{ property.name }}
              </option>
            </select>
          </div>
          
          <div class="filter-group">
            <label>Month:</label>
            <select v-model="selectedMonth" class="filter-select">
              <option value="">Select a month</option>
              <option v-for="month in availableMonths" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
          
          <div class="filter-actions">
            <button @click="applyFilters" class="btn btn-search">Search</button>
            <button @click="clearFilters" class="btn btn-outline">Clear Filters</button>
          </div>
        </div>

        <!-- Historical Reports Section - Only show when results are available -->
        <div v-if="searchResults.length > 0 && appliedFilters.month" class="historical-reports-section">
          <div class="property-group">
            <div class="property-header">
              <h3>{{ getSelectedPropertyName() || 'All Properties' }}</h3>
              <div class="property-stats">
                <span class="stat">{{ searchResults.length }} bookings</span>
                <span class="stat">{{ getTotalNights(searchResults) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(searchResults)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadRapportPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-rapport-excel">
                  Rapport Excel
                </button>
                <button @click="openReportModal(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="loadingHistory" class="loading-state">
          <div class="spinner"></div>
          <p>Loading reservation history...</p>
        </div>
        
        <div v-else class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th>Total Amount</th>
                <th>Services</th>
                <th>Booked At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reservation in paginatedHistory" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment }">
                <td class="property-name">{{ reservation.unitName }}</td>
                <td class="guest-name">
                  {{ reservation.guestName }}
                  <span v-if="reservation.isSegment && reservation.segmentInfo.total > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
                    SPLIT {{ reservation.segmentInfo.part }}/{{ reservation.segmentInfo.total }}
                  </span>
                </td>
                <td>
                  <span class="source-badge">{{ reservation.otaChannelName || 'Direct' }}</span>
                </td>
                <td>{{ formatDate(reservation.arrivalDate) }}</td>
                <td>{{ formatDate(reservation.departureDate) }}</td>
                <td class="text-center">{{ reservation.totalNights }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalServicesPrice) }}</td>
                <td>{{ formatDate(reservation.bookedAt) }}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="pagination" v-if="searchResults.length > itemsPerPage">
            <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
              Previous
            </button>
            <span class="pagination-info">
              Page {{ currentPage }} of {{ totalPages }} ({{ searchResults.length }} total)
            </span>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
              Next
            </button>
          </div>
          
          <div v-if="searchResults.length === 0 && (appliedFilters.property || appliedFilters.month)" class="empty-state">
            <p>No reservations found for the selected filters</p>
          </div>

          <div v-if="searchResults.length === 0 && !appliedFilters.property && !appliedFilters.month" class="empty-state">
            <p>Use the search filters above to find reservations</p>
          </div>
        </div>
      </div>
    </main>

    <!-- Report Modal -->
    <div v-if="showReportModal" class="report-modal-overlay" @click="closeReportModal">
      <div class="report-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title">
            <h2>{{ modalData.propertyName }} - {{ modalData.periodLabel }} Report</h2>
            <div class="modal-subtitle" v-if="hasUnsavedChanges">
              <span class="unsaved-indicator">●</span> Unsaved changes
            </div>
            <div class="modal-subtitle" v-else>
              <span class="saved-indicator">●</span> All changes saved
            </div>
          </div>
          <button @click="closeReportModal" class="close-btn">×</button>
        </div>

        <div class="modal-content">
          <!-- Settings Panel (Left Side) -->
          <div class="settings-panel">
            <div class="settings-header">
              <h3>Report Settings</h3>
              <p>Adjust parameters and see changes in real-time</p>
            </div>

            <div class="settings-form">
              <!-- Management Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Management Fee Configuration</h4>
                
                <div class="form-row">
                  <label class="form-label">Management Fee Rate (%)</label>
                  <div class="input-group">
                
                <input 
                    type="number" 
                    v-model="modalSettings.managementFeeRate" 
                    @input="handleNumericInput('managementFeeRate', $event); markAsModified(); calculateMetrics()"
                    @blur="ensureMinimumValue('managementFeeRate', 0)"
                    step="0.1" 
                    min="0" 
                    max="100"
                    class="form-input"
                  >
                    <span class="input-suffix">%</span>
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Calculation Base</label>
               <div class="radio-group">
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="revenue"
      @change="markAsModified; calculateMetrics()"
    >
    <span>Total Revenue (Gross Sales)</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="net"
      @change="markAsModified; calculateMetrics()"
    >
    <span>Net Income (after platform fees)</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="gross"
      @change="markAsModified; calculateMetrics()"
    >
    <span>Gross Profit (after all expenses) - percentage</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="modalSettings.managementFeeBase" 
      value="grossFixed"
      @change="markAsModified; calculateMetrics()"
    >
    <span>Gross Profit (after all expenses) - fixed amount</span>
  </label>
</div>

                <!-- Fixed Management Fee Amount (conditional) -->
                <div v-if="modalSettings.managementFeeBase === 'grossFixed'" class="form-row">
                  <label class="form-label">Fixed Management Fee Amount (£)</label>
                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input 
                      type="number" 
                      v-model="modalSettings.managementFeeFixedAmount" 
                      @input="handleNumericInput('managementFeeFixedAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('managementFeeFixedAmount', 0)"
                      step="1" 
                      min="0"
                      class="form-input"
                    >
                  </div>
                </div>

                </div>
              </div>

              <!-- VAT & Fixed Costs -->
              <div class="setting-group">
                <h4 class="subsection-title">VAT & Fixed Costs</h4>
                
                <div class="form-row">
                  <label class="form-label">VAT Rate (%)</label>
                  <div class="input-group">
                  <input 
                      type="number" 
                      v-model="modalSettings.vatRate" 
                      @input="handleNumericInput('vatRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('vatRate', 0)"
                      step="0.1" 
                      min="0" 
                      max="100"
                      class="form-input"
                    >
                    <span class="input-suffix">%</span>
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Maintenance (£)</label>
                  
                  <!-- Custom maintenance toggle -->
                  <div class="checkbox-row">
                    <label class="checkbox-option">
                      <input 
                        type="checkbox" 
                        v-model="modalSettings.useCustomMaintenance"
                        @change="markAsModified(); calculateMetrics()"
                      >
                      <span>Use custom amount for this month</span>
                    </label>
                  </div>
                  
                  <div class="input-group">
                    <span class="input-prefix">£</span>
                    <input 
                      type="number" 
                      v-model="modalSettings.customMaintenanceAmount" 
                      v-if="modalSettings.useCustomMaintenance"
                      @input="handleNumericInput('customMaintenanceAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customMaintenanceAmount', 0)"
                      step="1" 
                      min="0"
                      class="form-input"
                      placeholder="Custom amount for this month"
                    >
                    <input 
                      type="number" 
                      v-model="modalSettings.maintenance" 
                      v-else
                      @input="handleNumericInput('maintenance', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('maintenance', 0)"
                      step="1" 
                      min="0"
                      class="form-input"
                      placeholder="Default amount from settings"
                      readonly
                    >
                  </div>
                  
                  <p v-if="!modalSettings.useCustomMaintenance" class="setting-description">
                    Using default amount (£{{ modalSettings.maintenance }}) from property settings
                  </p>
                </div>

                <div class="form-row">
                  <label class="form-label">Consumables (£)</label>
                  <div class="input-group">
                    <span class="input-prefix">£</span>
                
                <input 
                  type="number" 
                  v-model="modalSettings.consumables" 
                  @input="handleNumericInput('consumables', $event); markAsModified(); calculateMetrics()"
                  @blur="ensureMinimumValue('consumables', 0)"
                  step="1" 
                  min="0"
                  class="form-input"
                >

                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">Other Expenses (£)</label>
                  <div class="input-group">
                    <span class="input-prefix">£</span>
              

              <input 
                type="number" 
                v-model="modalSettings.otherExpenses" 
                @input="handleNumericInput('otherExpenses', $event); markAsModified(); calculateMetrics()"
                @blur="ensureMinimumValue('otherExpenses', 0)"
                step="1" 
                min="0"
                class="form-input"
              >

                  </div>
                </div>
              </div>

              <!-- Cleaning Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Cleaning Fee Configuration</h4>
                
                <div class="form-row">
                  <label class="form-label">Cleaning Fee Mode</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cleaningMode" 
                        value="rentlio"
                        @change="markAsModified; calculateMetrics()"
                      >
                      <span>Use Rentlio Data</span>
                    </label>
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cleaningMode" 
                        value="monthly"
                        @change="markAsModified; calculateMetrics()"
                      >
                      <span>Custom Rates</span>
                    </label>
                  </div>
                </div>

                <div v-if="modalSettings.cleaningMode === 'monthly'" class="cleaning-custom-rates">
                  <div class="form-row">
                    <label class="form-label">Booking.com Rate (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                   
                   <input 
          type="number" 
          v-model="modalSettings.bookingCleaningFee" 
          @input="handleNumericInput('bookingCleaningFee', $event); markAsModified(); calculateMetrics()"
          @blur="ensureMinimumValue('bookingCleaningFee', 0)"
          step="1" 
          min="0"
          class="form-input"
        >
                              <span class="input-suffix">per booking</span>
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">Other Platforms Rate (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                   

                   <input 
                      type="number" 
                      v-model="modalSettings.otherCleaningFee" 
                      @input="handleNumericInput('otherCleaningFee', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('otherCleaningFee', 0)"
                      step="1" 
                      min="0"
                      class="form-input"
                    >
                            
                            <span class="input-suffix">per booking</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Card Fee Configuration -->
              <div class="setting-group">
                <h4 class="subsection-title">Card Fee Configuration</h4>
                
                <div class="form-row">
                  <label class="form-label">Card Fee Mode</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cardFeeMode" 
                        value="rentlio"
                        @change="markAsModified; calculateMetrics()"
                      >
                      <span>Use Rentlio Data</span>
                    </label>
                    <label class="radio-option">
                      <input 
                        type="radio" 
                        v-model="modalSettings.cardFeeMode" 
                        value="custom"
                        @change="markAsModified; calculateMetrics()"
                      >
                      <span>Custom Rate</span>
                    </label>
                  </div>
                </div>

                <div v-if="modalSettings.cardFeeMode === 'custom'" class="card-custom-rates">
                  <div class="form-row">
                    <label class="form-label">Card Fee Rate (%)</label>
                    <div class="input-group">
                   
                   <input 
                      type="number" 
                      v-model="modalSettings.customCardFeeRate" 
                      @input="handleNumericInput('customCardFeeRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeRate', 0)"
                      step="0.01" 
                      min="0" 
                      max="10"
                      class="form-input"
                    >


                      <span class="input-suffix">%</span>
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">Fixed Fee (£)</label>
                    <div class="input-group">
                      <span class="input-prefix">£</span>
                      
                      <input 
                      type="number" 
                      v-model="modalSettings.customCardFeeFixed" 
                      @input="handleNumericInput('customCardFeeFixed', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeFixed', 0)"
                      step="0.01" 
                      min="0"
                      class="form-input"
                    >


                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Settings Actions -->
            <div class="settings-actions">
              <button @click="resetToSaved" :disabled="!hasUnsavedChanges" class="btn btn-outline">
                Reset to Saved
              </button>
              <button @click="saveSettings" :disabled="!hasUnsavedChanges || savingSettings" class="btn btn-primary">
                <span v-if="savingSettings">Saving...</span>
                <span v-else>Save Settings</span>
              </button>
            </div>
          </div>

          <!-- Report Preview (Right Side) -->
          <div class="preview-panel">
            <div class="preview-header">
              <h3>Report Preview</h3>
              <div class="preview-actions">
                <button @click="downloadPDF" :disabled="generatingPDF" class="btn btn-pdf">
                  <span v-if="generatingPDF">Generating...</span>
                  <span v-else>Download PDF</span>
                </button>
                <button @click="sendReportByEmailFromModal" class="btn btn-email" style="margin-left: 10px;">
                  📧 Send by Email
                </button>
              </div>
            </div>

<div class="preview-content" style="background: #ffffff; padding: 10px; overflow-y: auto; overflow-x: hidden;">
  <div class="pdf-preview-wrapper" style="
    width: 210mm; 
    min-height: 297mm; 
    background: white; 
    margin: 0 auto; 
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    transform-origin: top center;
    transform: scale(1.3);
    border: 1px solid #e0e0e0;
  ">
    <div v-html="previewHTML" style="width: 100%; height: 100%;"></div>
  </div>
</div>
</div>
        </div>
      </div>
    </div>

    <!-- Professional Contacts Management Modal -->
    <div v-if="showContactsModal" class="contacts-modal-overlay" @click="closeContactsModal">
      <div class="contacts-modal-container" @click.stop>
        <!-- Header -->
        <div class="contacts-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="contacts-icon">👥</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Email Contacts</h2>
              <p>Manage your email recipients for reports</p>
            </div>
          </div>
          <button @click="closeContactsModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="contacts-modal-body">
          <!-- Add New Contact Section -->
          <div class="add-contact-card">
            <div class="card-header">
              <h3>➕ Add New Contact</h3>
              <p>Create a new email contact for sending reports</p>
            </div>
            <div class="card-content">
              <div class="add-contact-form">
                <div class="input-group">
                  <div class="input-container">
                    <label class="input-label">👤 Contact Name</label>
                    <input 
                      v-model="newContact.name" 
                      placeholder="Enter full name..." 
                      class="professional-input"
                      type="text"
                    >
                  </div>
                  <div class="input-container">
                    <label class="input-label">📧 Email Address</label>
                    <input 
                      v-model="newContact.email" 
                      placeholder="Enter email address..." 
                      class="professional-input"
                      type="email"
                    >
                  </div>
                </div>
                <button 
                  @click="addContact" 
                  :disabled="!newContact.name || !newContact.email"
                  class="add-contact-btn"
                >
                  <span class="btn-icon">✨</span>
                  <span>Add Contact</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Contacts List Section -->
          <div class="contacts-list-card">
            <div class="card-header">
              <div class="header-with-count">
                <h3>📋 Saved Contacts</h3>
                <span class="contact-count">{{ contacts.length }} contact{{ contacts.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>
            
            <div class="card-content">
              <!-- Empty State -->
              <div v-if="contacts.length === 0" class="empty-contacts-state">
                <div class="empty-icon">📭</div>
                <h4>No contacts yet</h4>
                <p>Add your first email contact above to start sending reports</p>
              </div>
              
              <!-- Contacts Grid -->
              <div v-else class="contacts-grid">
                <div 
                  v-for="contact in contacts" 
                  :key="contact.id" 
                  class="contact-item"
                  :class="{ 'editing': editingContact?.id === contact.id }"
                >
                  <!-- Contact Avatar -->
                  <div class="contact-avatar">
                    {{ contact.name.charAt(0).toUpperCase() }}
                  </div>
                  
                  <!-- Contact Info / Edit Form -->
                  <div class="contact-details">
                    <div v-if="editingContact?.id === contact.id" class="edit-form">
                      <input 
                        v-model="editingContact.name" 
                        class="edit-input"
                        placeholder="Contact name..."
                      >
                      <input 
                        v-model="editingContact.email" 
                        class="edit-input"
                        type="email"
                        placeholder="Email address..."
                      >
                    </div>
                    <div v-else class="contact-info">
                      <div class="contact-name">{{ contact.name }}</div>
                      <div class="contact-email">{{ contact.email }}</div>
                      <div class="contact-meta">
                        Added {{ formatContactDate(contact.createdAt) }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Actions -->
                  <div class="contact-actions">
                    <div v-if="editingContact?.id === contact.id" class="edit-actions">
                      <button @click="saveEditContact" class="action-btn save-btn">
                        <span>💾</span>
                      </button>
                      <button @click="editingContact = null" class="action-btn cancel-btn">
                        <span>❌</span>
                      </button>
                    </div>
                    <div v-else class="view-actions">
                      <button @click="editContact(contact)" class="action-btn edit-btn">
                        <span>✏️</span>
                      </button>
                      <button @click="deleteContact(contact.id)" class="action-btn delete-btn">
                        <span>🗑️</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="contacts-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">💾</span>
              Contacts are saved locally in your browser
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeContactsModal" class="close-modal-btn">
              <span class="btn-icon">✓</span>
              <span>Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Professional Email Sending Modal -->
    <div v-if="showEmailModal" class="email-modal-overlay" @click="closeEmailModal">
      <div class="email-modal-container" @click.stop>
        <!-- Header -->
        <div class="email-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="email-icon">📧</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Send Report via Email</h2>
              <p>Share your property report with stakeholders</p>
            </div>
          </div>
          <button @click="closeEmailModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="email-modal-body">
          <!-- Report Preview Card -->
          <div class="report-preview-card">
            <div class="card-header">
              <h3>📊 Report Summary</h3>
            </div>
            <div class="card-content">
              <div class="report-details">
                <div class="detail-item">
                  <div class="detail-icon">🏨</div>
                  <div class="detail-content">
                    <span class="detail-label">Property</span>
                    <span class="detail-value">{{ emailData.propertyName }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📅</div>
                  <div class="detail-content">
                    <span class="detail-label">Period</span>
                    <span class="detail-value">{{ emailData.period }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📋</div>
                  <div class="detail-content">
                    <span class="detail-label">Report Type</span>
                    <span class="detail-value">{{ emailData.reportType }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recipient Selection -->
          <div class="recipient-section">
            <div class="section-title">
              <h3>👤 Select Recipient</h3>
              <p>Choose who will receive this report</p>
            </div>
            
            <div class="recipient-selector">
              <div class="contact-dropdown">
                <select v-model="emailData.contact" class="professional-select">
                  <option :value="null" disabled>Choose a contact...</option>
                  <option v-for="contact in contacts" :key="contact.id" :value="contact">
                    {{ contact.name }} • {{ contact.email }}
                  </option>
                </select>
              </div>
              
              <button @click="openContactsModal" class="manage-contacts-btn">
                <span class="btn-icon">👥</span>
                <span>Manage Contacts</span>
              </button>
            </div>

            <!-- Selected Contact Preview -->
            <div v-if="emailData.contact" class="selected-contact-preview">
              <div class="contact-card">
                <div class="contact-avatar">
                  {{ emailData.contact.name.charAt(0).toUpperCase() }}
                </div>
                <div class="contact-info">
                  <div class="contact-name">{{ emailData.contact.name }}</div>
                  <div class="contact-email">{{ emailData.contact.email }}</div>
                </div>
                <div class="contact-status">
                  <span class="status-badge">✓ Selected</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Email Options -->
          <div class="email-options-section">
            <div class="section-title">
              <h3>⚙️ Email Options</h3>
            </div>
            
            <div class="options-grid">
              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">📎</span>
                  <span class="option-title">Include Attachments</span>
                </div>
                <div class="option-content">
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includeExcel" checked>
                    <span class="checkmark"></span>
                    Excel Report
                  </label>
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includePDF">
                    <span class="checkmark"></span>
                    PDF Report
                  </label>
                </div>
              </div>

              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">💬</span>
                  <span class="option-title">Message</span>
                </div>
                <div class="option-content">
                  <textarea 
                    v-model="emailOptions.message" 
                    class="message-textarea"
                    placeholder="Add a personal message (optional)..."
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="email-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">🔒</span>
              Your data is sent securely
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeEmailModal" class="cancel-btn">
              Cancel
            </button>
            <button 
              @click="sendReportByEmail" 
              :disabled="!emailData.contact || sendingEmail"
              :class="['send-btn', { 'disabled': !emailData.contact || sendingEmail }]"
            >
              <span v-if="sendingEmail">📧 Sending...</span>
              <span v-else>Send Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Sent Popup -->
    <div v-if="showEmailSentPopup" class="email-sent-popup-overlay">
      <div class="email-sent-popup">
        <div class="popup-icon">
          <div class="success-checkmark">
            <div class="check-icon">
              <span class="icon-line line-tip"></span>
              <span class="icon-line line-long"></span>
              <div class="icon-circle"></div>
              <div class="icon-fix"></div>
            </div>
          </div>
        </div>
        
        <div class="popup-content">
          <h2>Email Sent Successfully!</h2>
          <p>Your report has been sent to <strong>{{ emailData.contact?.name }}</strong></p>
          <p class="delivery-info">The email will arrive in the recipient's inbox within the next 5 minutes.</p>
        </div>
        
        <button @click="closeEmailSentPopup" class="popup-close-btn">
          Got it
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import '../../../upload-service.js';
import * as XLSX from 'xlsx';
import SETTINGS from '@/config/settings.js';

export default {
  name: 'ReservationsView',
  data() {
    return {
      // Données existantes
      isCollapsed: false,
      loading: false,
      loadingLastMonth: false,
      loadingHistory: false,
      currentMonthReservations: [],
      lastMonthReservations: [],
      historyReservations: [],
      properties: [],
      selectedCurrentProperty: '31510',
      selectedLastMonthProperty: '31510', 
      selectedProperty: '',
      selectedMonth: '',
      appliedFilters: {
        property: '',
        month: ''
      },
      searchResults: [],
      currentPage: 1,
      itemsPerPage: 20,
      baseUrl: SETTINGS.API.baseUrl,

      // Modal state
      showReportModal: false,
      modalData: {
        propertyName: '',
        periodLabel: '',
        reservations: [],
        realPropertyId: null // AJOUTER cette ligne

      },
      modalSettings: {
        managementFeeRate: 16,
        managementFeeBase: 'net',
        managementFeeFixedAmount: 500,
        vatRate: 20,
        maintenance: 0,
        useCustomMaintenance: false,
        customMaintenanceAmount: 0,
        consumables: 20,
        otherExpenses: 0,
        cleaningMode: 'rentlio',
        bookingCleaningFee: 150,
        otherCleaningFee: 120,
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30
      },
      originalSettings: {},
      calculatedMetrics: {},
      previewHTML: '',
      hasUnsavedChanges: false,
      savingSettings: false,
      generatingPDF: false,

      // Email contacts management
      contacts: [],
      showContactsModal: false,
      showEmailModal: false,
      showEmailSentPopup: false,
      selectedContact: null,
      newContact: {
        name: '',
        email: ''
      },
      editingContact: null,
      emailData: {
        contact: null,
        reportType: '',
        propertyName: '',
        period: ''
      },
      emailOptions: {
        includeExcel: false,
        includePDF: true,
        message: ''
      },
      sendingEmail: false
    };
  },

 computed: {
  groupedCurrentMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedCurrentProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.currentMonthReservations };
  },
  
  groupedLastMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedLastMonthProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.lastMonthReservations };
  },
  
  availableMonths() {
    const months = [];
    const now = new Date();
    
    // Ajouter les 12 derniers mois
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().substring(0, 7); // Format YYYY-MM
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      let type = 'historical';
      if (i === 0) type = 'current';
      else if (i === 1) type = 'lastmonth';
      
      months.push({
        value: value,
        label: label,
        type: type
      });
    }
    
    return months;
  },

  paginatedHistory() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.searchResults.slice(start, end);
  },
  
  totalPages() {
    return Math.ceil(this.searchResults.length / this.itemsPerPage);
  }
},

  methods: {
    // Méthodes existantes
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    getSegmentTooltip(reservation) {
  if (!reservation.isSegment || !reservation.segmentInfo) {
    return '';
  }
  
  const info = reservation.segmentInfo;
  return `Multi-month reservation split - Part ${info.part} of ${info.total}\n` +
         `Original reservation: ${info.originalTotalNights} nights, £${info.originalTotalPrice}\n` +
         `Original ID: ${info.originalId}`;
},
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },
    
    getCurrentMonth() {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    
    getLastMonth() {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    

    
    getTotalNights(reservations) {
      return reservations.reduce((total, r) => total + r.totalNights, 0);
    },
    
    getTotalRevenue(reservations) {
      return reservations.reduce((total, r) => total + r.totalReservationPrice, 0);
    },

    getSelectedPropertyName() {
      if (!this.selectedProperty) {
        return null;
      }
      const property = this.properties.find(p => p.id.toString() === this.selectedProperty.toString());
      return property ? property.name : null;
    },

    getSelectedMonthLabel() {
      if (!this.selectedMonth) {
        return 'Historical Month';
      }
      const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
      return selectedMonthData ? selectedMonthData.label : 'Historical Month';
    },


    // Modal methods


    closeReportModal() {
      if (this.hasUnsavedChanges) {
        if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
          return;
        }
      }

      this.showReportModal = false;
      this.modalData = {};
      this.modalSettings = {};
      this.originalSettings = {};
      this.calculatedMetrics = {};
      this.previewHTML = '';
      this.hasUnsavedChanges = false;
    },

    markAsModified() {
      this.hasUnsavedChanges = true;
    },

    calculateMetrics() {
      if (!this.modalData.reservations || this.modalData.reservations.length === 0) {
        return;
      }

      const reservations = this.modalData.reservations;
      const settings = this.modalSettings;

      let totalRevenue = 0;
      let totalNights = 0;
      let totalPlatformFees = 0;
      let totalCardFees = 0;
      let totalOutgoings = 0;

      reservations.forEach(reservation => {
        const amount = parseFloat(reservation.totalReservationPrice || 0);
        const fees = parseFloat(reservation.channelCommissionNominal || 0);
        
        let cardFee = 0;
        if (settings.cardFeeMode === 'rentlio') {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
        } else {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? 
            (amount * settings.customCardFeeRate / 100) : 0;
        }
        
        let outgoings = 0;
        const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');
        
        if (isExtension) {
          outgoings = 0;
        } else {
          if (settings.cleaningMode === 'rentlio') {
            outgoings = parseFloat(reservation.totalServicesPrice || 0);
          } else {
            const platform = reservation.otaChannelName || '';
            if (platform === 'Booking.com') {
              outgoings = settings.bookingCleaningFee;
            } else {
              outgoings = settings.otherCleaningFee;
            }
          }
        }

        totalRevenue += amount;
       totalNights += parseInt(reservation.totalNights || 0);
       totalPlatformFees += fees;
       totalCardFees += cardFee;
       totalOutgoings += outgoings;
     });

     const netIncome = totalRevenue - totalPlatformFees - totalCardFees;
     const grossProfit = netIncome - totalOutgoings;
     
     let managementFees = 0;
      if (settings.managementFeeBase === 'revenue') {
        managementFees = totalRevenue * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'gross') {
        managementFees = grossProfit * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'grossFixed') {
        managementFees = parseFloat(settings.managementFeeFixedAmount || 0);
      } else {
        managementFees = netIncome * (settings.managementFeeRate / 100);
      }
     
     const vat = managementFees * (settings.vatRate / 100);
     const maintenance = settings.useCustomMaintenance 
       ? parseFloat(settings.customMaintenanceAmount || 0)
       : parseFloat(settings.maintenance || 0);
     const consumables = parseFloat(settings.consumables || 0);
     const otherExpenses = parseFloat(settings.otherExpenses || 0);
     const settlementAmount = grossProfit - managementFees - vat - maintenance - consumables - otherExpenses;

     this.calculatedMetrics = {
       totalRevenue,
       totalNights,
       totalPlatformFees,
       totalCardFees,
       totalOutgoings,
       netIncome,
       grossProfit,
       managementFees,
       vat,
       maintenance,
       consumables,
       otherExpenses,
       settlementAmount,
       averagePerNight: reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0
     };

     this.generatePreviewHTML();
   },

generatePreviewHTML() {
  if (!this.modalData.reservations || !this.calculatedMetrics) {
    return;
  }

  const reservations = this.modalData.reservations;
  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;

  const currentDate = new Date();
  const monthYear = this.modalData.periodLabel === 'Current Month' ? 
    currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) :
    new Date(currentDate.getFullYear(), currentDate.getMonth() - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = parseFloat(reservation.channelCommissionNominal || 0);
    
    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? 
        (amount * settings.customCardFeeRate / 100) : 0;
    }
    
    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');
    
    if (isExtension) {
      outgoings = 0;
    } else {
      if (settings.cleaningMode === 'rentlio') {
        outgoings = parseFloat(reservation.totalServicesPrice || 0);
      } else {
        const platform = reservation.otaChannelName || '';
        if (platform === 'Booking.com') {
          outgoings = settings.bookingCleaningFee || 150;
        } else {
          outgoings = settings.otherCleaningFee || 120;
        }
      }
    }
    
    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;
    const perNight = reservation.totalNights > 0 ? amount / reservation.totalNights : 0;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
        <td style="text-align: right;">£${outgoings.toFixed(2)}</td>
        <td style="text-align: right;">£${grossProfit.toFixed(2)}</td>
        <td style="text-align: right;">£${perNight.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // HTML STRICTEMENT IDENTIQUE AU PDF
  this.previewHTML = `
    <div style="
      font-family: 'Playfair Display', 'Times New Roman', serif;
      background: #f8f9f9;
      color: #2c3e50;
      font-size: 12px;
      line-height: 1.4;
      width: 210mm;
      min-height: 297mm;
      padding: 15mm;
      position: relative;
      box-sizing: border-box;
    ">
      <div style="
        width: 100%;
        min-height: 267mm;
        display: flex;
        flex-direction: column;
        padding: 20px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        position: relative;
      ">
        <div style="margin-bottom: 25px;">
          <h1 style="
            font-size: 38px;
            font-weight: 400;
            color: #2c3e50;
            margin: 0 0 5px 0;
            letter-spacing: 1px;
          ">Monthly Report</h1>
          <h2 style="
            font-size: 22px;
            font-weight: 300;
            color: #34495e;
            margin: 0 0 15px 0;
            letter-spacing: 0.5px;
          ">${this.modalData.propertyName}</h2>
          <div style="
            width: 100%;
            height: 1px;
            background: #bdc3c7;
            margin: 15px 0 12px 0;
          "></div>
          <div style="
            font-size: 13px;
            font-weight: 400;
            color: #7f8c8d;
            text-transform: uppercase;
            letter-spacing: 2px;
          ">${monthYear.toUpperCase()}</div>
        </div>
        
        <div style="
          font-size: 10px;
          font-weight: 600;
          color: #2c3e50;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin: 20px 0 10px 0;
        ">BOOKING OVERVIEW</div>
        
        <table style="
          width: 100%;
          border-collapse: collapse;
          font-size: 8px;
          margin-bottom: 15px;
          font-family: 'Arial', sans-serif;
        ">
          <thead>
            <tr>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Booking Name</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Source</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">No. Nights</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Amount</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Fees</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Card Fee</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Net Income</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Outgoings</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Gross Profit</th>
              <th style="
                background: #ecf0f1;
                padding: 5px 3px;
                text-align: center;
                font-weight: 600;
                border: 1px solid #bdc3c7;
                font-size: 7px;
                line-height: 1.2;
                color: #2c3e50;
              ">Per Night</th>
            </tr>
          </thead>
          <tbody>
            ${bookingsRows.split('\n').map(line => line.trim()).filter(line => line).map(line => 
              line.replace('<td', '<td style="padding: 3px 2px; border: 1px solid #d5dbdb; font-size: 7px; vertical-align: middle; color: #2c3e50;"')
            ).join('')}
            <tr style="background: #f8f9fa; font-weight: 700; border-top: 2px solid #34495e;">
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: left;"><strong>Totals</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700;"></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: center;"><strong>${totalNights}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalAmount.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalFees.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalCardFees.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalNetIncome.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalOutgoings.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${totalGrossProfit.toFixed(2)}</strong></td>
              <td style="padding: 3px 2px; border: 1px solid #34495e; font-size: 7px; vertical-align: middle; color: #2c3e50; font-weight: 700; text-align: right;"><strong>£${avgPerNight.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div style="margin: 15px 0;">
          <div style="margin-bottom: 20px; margin-top: 20px;">
            <div style="
              font-size: 10px;
              font-weight: 700;
              margin-bottom: 8px;
              text-transform: uppercase;
              color: #2c3e50;
              letter-spacing: 1px;
            ">INCOME & DIRECT EXPENSES</div>
            <table style="
              width: 100%;
              border-collapse: collapse;
              font-family: 'Arial', sans-serif;
            ">
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">TOTAL GUEST PAYMENTS</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.totalRevenue.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">PLATFORM FEES</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.totalPlatformFees.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">CARD PROCESSING FEES</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.totalCardFees.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">CLEANING & LAUNDRY</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.totalOutgoings.toFixed(2)}</td>
              </tr>
              <tr style="border-top: 1px solid #34495e; padding-top: 4px; margin-top: 4px; font-weight: 700; font-size: 11px;">
                <td style="padding-top: 4px; border-top: 1px solid #34495e; font-size: 8px; color: #2c3e50; text-align: left; width: 70%;">GROSS PROFIT</td>
                <td style="padding-top: 4px; border-top: 1px solid #34495e; font-size: 8px; color: #2c3e50; text-align: right; width: 30%; font-weight: 600;">£${calculations.grossProfit.toFixed(2)}</td>
              </tr>
            </table>
          </div>
          
          <div style="margin-bottom: 20px; margin-top: 20px;">
            <div style="
              font-size: 10px;
              font-weight: 700;
              margin-bottom: 8px;
              text-transform: uppercase;
              color: #2c3e50;
              letter-spacing: 1px;
            ">MANAGEMENT FEES & OTHER COSTS</div>
            <table style="
              width: 100%;
              border-collapse: collapse;
              font-family: 'Arial', sans-serif;
            ">
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">${managementFeeLabel}</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.managementFees.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">VAT</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.vat.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">CONSUMABLES</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.consumables.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">MAINTENANCE</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.maintenance.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: left; width: 70%;">OTHER EXPENSES</td>
                <td style="padding: 2px 0; font-size: 8px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.otherExpenses.toFixed(2)}</td>
              </tr>
            </table>
          </div>
        </div>
        
        <div style="
          margin: 25px 0;
          padding: 12px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          text-align: center;
        ">
          <div style="
            font-size: 11px;
            font-weight: 700;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #2c3e50;
          ">SETTLEMENT SUMMARY</div>
          <table style="
            width: 100%;
            border-collapse: collapse;
            font-family: 'Arial', sans-serif;
          ">
            <tr>
              <td style="padding: 3px 0; font-size: 11px; color: #2c3e50; border: none; text-align: left; width: 70%;">GROSS PROFIT</td>
              <td style="padding: 3px 0; font-size: 11px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${calculations.grossProfit.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 3px 0; font-size: 11px; color: #2c3e50; border: none; text-align: left; width: 70%;">LESS MANAGEMENT FEE & OTHER COSTS</td>
              <td style="padding: 3px 0; font-size: 11px; color: #2c3e50; border: none; text-align: right; width: 30%; font-weight: 600;">£${(calculations.managementFees + calculations.vat + calculations.maintenance + calculations.consumables + calculations.otherExpenses).toFixed(2)}</td>
            </tr>
            <tr style="border-top: 2px solid #2c3e50; padding-top: 6px; margin-top: 6px; font-weight: 700; font-size: 13px;">
              <td style="padding-top: 6px; border-top: 2px solid #2c3e50; font-size: 13px; color: #2c3e50; text-align: left; width: 70%;">SETTLEMENT TO OWNER</td>
              <td style="padding-top: 6px; border-top: 2px solid #2c3e50; font-size: 13px; color: #2c3e50; text-align: right; width: 30%; font-weight: 700;">£${calculations.settlementAmount.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        
        <div style="
          margin-top: auto;
          padding-top: 15px;
          text-align: center;
          border-top: 1px solid #bdc3c7;
        ">
          <div style="margin-bottom: 12px;">
            <div style="font-size: 8px; color: #7f8c8d; margin-bottom: 2px;">Prepared by</div>
            <div style="font-size: 10px; font-weight: 600; color: #2c3e50;">Sojourn Executive</div>
          </div>
          
          <div style="margin-bottom: 15px;">
            <div style="
              font-size: 16px;
              font-weight: 300;
              letter-spacing: 6px;
              color: #2c3e50;
              margin-bottom: 1px;
            ">SOJOURN</div>
            <div style="
              font-size: 6px;
              color: #95a5a6;
              text-transform: uppercase;
              letter-spacing: 3px;
            ">EXECUTIVE</div>
          </div>
          
          <div style="
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 9px;
            color: #7f8c8d;
            margin-top: 10px;
          ">
            <div>${new Date().getFullYear()}</div>
            <div>1</div>
          </div>
        </div>
      </div>
    </div>
  `;
},

   resetToSaved() {
     if (!confirm('Reset all settings to saved values?')) {
       return;
     }

     this.modalSettings = { ...this.originalSettings };
     this.hasUnsavedChanges = false;
     this.calculateMetrics();
   },

async saveSettings() {
  this.savingSettings = true;
  
  try {
    const propertyId = this.modalData.realPropertyId;
    
    if (!propertyId) {
      throw new Error('ID de propriété manquant');
    }

    const savedSettings = localStorage.getItem('propertySettings');
    let allSettings = {};
    
    if (savedSettings) {
      try {
        allSettings = JSON.parse(savedSettings);
      } catch (e) {
        allSettings = {};
      }
    }
    
    allSettings[propertyId] = { ...this.modalSettings };
    localStorage.setItem('propertySettings', JSON.stringify(allSettings));

    this.originalSettings = { ...this.modalSettings };
    this.hasUnsavedChanges = false;

    alert(`Settings saved for ${this.modalData.propertyName}!`);
    
  } catch (error) {
    console.error('Error saving settings:', error);
    alert('Save failed. Please try again.');
  } finally {
    this.savingSettings = false;
  }
},

   async downloadPDF() {
     this.generatingPDF = true;
     
     try {
       const jsPDF = (await import('jspdf')).default;
       const html2canvas = (await import('html2canvas')).default;

       const iframe = document.createElement('iframe');
       iframe.style.cssText = `
         position: absolute !important;
         top: -10000px !important;
         left: -10000px !important;
         width: 210mm !important;
         height: 297mm !important;
         border: none !important;
         opacity: 0 !important;
         pointer-events: none !important;
       `;
       
       document.body.appendChild(iframe);
       
       const pdfHTML = this.generatePDFHTML();
       iframe.contentDocument.open();
       iframe.contentDocument.write(pdfHTML);
       iframe.contentDocument.close();
       
       await new Promise(resolve => {
         iframe.onload = resolve;
         setTimeout(resolve, 500);
       });

       const canvas = await html2canvas(iframe.contentDocument.body, {
         scale: 2,
         useCORS: true,
         backgroundColor: '#ffffff',
         width: 794,
         height: 1123,
         logging: false
       });

       const pdf = new jsPDF('p', 'mm', 'a4');
       const imgData = canvas.toDataURL('image/png');
       
       const imgWidth = 210;
       const pageHeight = 295;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;
       let heightLeft = imgHeight;
       let position = 0;

       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;

       while (heightLeft >= 0) {
         position = heightLeft - imgHeight;
         pdf.addPage();
         pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
         heightLeft -= pageHeight;
       }

       const filename = `${this.modalData.propertyName}_${this.modalData.periodLabel}_Report.pdf`;
       pdf.save(filename);

       document.body.removeChild(iframe);

     } catch (error) {
       console.error('Error generating PDF:', error);
       alert('Failed to generate PDF. Please try again.');
     } finally {
       this.generatingPDF = false;
     }
   },

   generatePDFHTML() {
  // Utiliser EXACTEMENT le même HTML que la méthode generateHTMLContent existante
  const currentDate = new Date();
  const monthYear = this.modalData.periodLabel === 'Current Month' ? 
    currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) :
    new Date(currentDate.getFullYear(), currentDate.getMonth() - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;
  const reservations = this.modalData.reservations;

  // Générer les lignes du tableau EXACTEMENT comme dans l'original
  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = parseFloat(reservation.channelCommissionNominal || 0);
    
    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? 
        (amount * settings.customCardFeeRate / 100) : 0;
    }
    
    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');
    
    if (isExtension) {
      outgoings = 0;
    } else {
      if (settings.cleaningMode === 'rentlio') {
        outgoings = parseFloat(reservation.totalServicesPrice || 0);
      } else {
        const platform = reservation.otaChannelName || '';
        if (platform === 'Booking.com') {
          outgoings = settings.bookingCleaningFee || 150;
        } else {
          outgoings = settings.otherCleaningFee || 120;
        }
      }
    }
    
    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;
    const perNight = reservation.totalNights > 0 ? amount / reservation.totalNights : 0;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
        <td style="text-align: right;">£${outgoings.toFixed(2)}</td>
        <td style="text-align: right;">£${grossProfit.toFixed(2)}</td>
        <td style="text-align: right;">£${perNight.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // UTILISER EXACTEMENT LES MÊMES STYLES CSS QUE L'ORIGINAL
  return `
<!DOCTYPE html>
<html lang="en">
<head>
   <meta charset="UTF-8">
   <title>Monthly Report</title>
   <style>
       @page {
           size: A4;
           margin: 0;
       }
       
       * {
           margin: 0;
           padding: 0;
           box-sizing: border-box;
       }
       
       body {
           font-family: 'Playfair Display', 'Times New Roman', serif;
           background: #f8f9f9;
           color: #2c3e50;
           font-size: 12px;
           line-height: 1.4;
           width: 210mm;
           height: 297mm;
           padding: 15mm;
           position: relative;
       }
       
       .page-container {
           width: 100%;
           min-height: 267mm;
           display: flex;
           flex-direction: column;
           padding: 20px;
           box-shadow: 0 2px 4px rgba(0,0,0,0.1);
           position: relative;
       }
       
       .report-header {
           margin-bottom: 25px;
       }
       
       .report-title {
           font-size: 38px;
           font-weight: 400;
           color: #2c3e50;
           margin-bottom: 5px;
           letter-spacing: 1px;
       }
       
       .property-name {
           font-size: 22px;
           font-weight: 300;
           color: #34495e;
           margin-bottom: 15px;
           letter-spacing: 0.5px;
       }
       
       .divider {
           width: 100%;
           height: 1px;
           background: #bdc3c7;
           margin: 15px 0 12px 0;
       }
       
       .report-period {
           font-size: 13px;
           font-weight: 400;
           color: #7f8c8d;
           text-transform: uppercase;
           letter-spacing: 2px;
       }
       
       .section-title {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
           text-transform: uppercase;
           letter-spacing: 1px;
           margin: 20px 0 10px 0;
       }
       
       .data-table {
           width: 100%;
           border-collapse: collapse;
           font-size: 8px;
           margin-bottom: 15px;
           font-family: 'Arial', sans-serif;
       }
       
       .data-table th {
           background: #ecf0f1;
           padding: 5px 3px;
           text-align: center;
           font-weight: 600;
           border: 1px solid #bdc3c7;
           font-size: 7px;
           line-height: 1.2;
           color: #2c3e50;
       }
       
       .data-table td {
           padding: 3px 2px;
           border: 1px solid #d5dbdb;
           font-size: 7px;
           vertical-align: middle;
           color: #2c3e50;
       }
       
       .data-table .totals-row {
           background: #f8f9fa;
           font-weight: 700;
           border-top: 2px solid #34495e;
       }
       
       .data-table .totals-row td {
           border: 1px solid #34495e;
           font-weight: 700;
           color: #2c3e50;
       }
       
       .content-sections {
           margin: 15px 0;
       }
       
       .summary-section {
           margin-bottom: 20px;
           margin-top: 20px;
       }
       
       .summary-title {
           font-size: 10px;
           font-weight: 700;
           margin-bottom: 8px;
           text-transform: uppercase;
           color: #2c3e50;
           letter-spacing: 1px;
       }
       
       .summary-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .summary-table td {
           padding: 2px 0;
           font-size: 8px;
           color: #2c3e50;
           border: none;
       }
       
       .summary-table .label {
           text-align: left;
           width: 70%;
       }
       
       .summary-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .summary-table .total-row {
           border-top: 1px solid #34495e;
           padding-top: 4px;
           margin-top: 4px;
           font-weight: 700;
           font-size: 11px;
       }
       
       .summary-table .total-row td {
           padding-top: 4px;
           border-top: 1px solid #34495e;
       }
       
       .settlement-section {
           margin: 25px 0;
           padding: 12px;
           background: #f8f9fa;
           border: 1px solid #e9ecef;
           text-align: center;
       }
       
       .settlement-title {
           font-size: 11px;
           font-weight: 700;
           margin-bottom: 10px;
           text-transform: uppercase;
           letter-spacing: 1px;
           color: #2c3e50;
       }
       
       .settlement-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .settlement-table td {
           padding: 3px 0;
           font-size: 11px;
           color: #2c3e50;
           border: none;
       }
       
       .settlement-table .label {
           text-align: left;
           width: 70%;
       }
       
       .settlement-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .settlement-table .final-row {
           border-top: 2px solid #2c3e50;
           padding-top: 6px;
           margin-top: 6px;
           font-weight: 700;
           font-size: 13px;
       }
       
       .settlement-table .final-row td {
           padding-top: 6px;
           border-top: 2px solid #2c3e50;
       }
       
       .footer-section {
           margin-top: auto;
           padding-top: 15px;
           text-align: center;
           border-top: 1px solid #bdc3c7;
       }
       
       .prepared-info {
           margin-bottom: 12px;
       }
       
       .prepared-label {
           font-size: 8px;
           color: #7f8c8d;
           margin-bottom: 2px;
       }
       
       .prepared-company {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
       }
       
       .company-logo {
           margin-bottom: 15px;
       }
       
       .company-name {
           font-size: 16px;
           font-weight: 300;
           letter-spacing: 6px;
           color: #2c3e50;
           margin-bottom: 1px;
       }
       
       .company-tagline {
           font-size: 6px;
           color: #95a5a6;
           text-transform: uppercase;
           letter-spacing: 3px;
       }
       
       .footer-meta {
           display: flex;
           justify-content: space-between;
           align-items: center;
           font-size: 9px;
           color: #7f8c8d;
           margin-top: 10px;
       }
   </style>
</head>
<body>
   <div class="page-container">
       <div class="report-header">
           <h1 class="report-title">Monthly Report</h1>
           <h2 class="property-name">${this.modalData.propertyName}</h2>
           <div class="divider"></div>
           <div class="report-period">${monthYear.toUpperCase()}</div>
       </div>
       
       <div class="section-title">BOOKING OVERVIEW</div>
       
       <table class="data-table">
           <thead>
               <tr>
                   <th>Booking Name</th>
                   <th>Source</th>
                   <th>No. Nights</th>
                   <th>Amount</th>
                   <th>Fees</th>
                   <th>Card Fee</th>
                   <th>Net Income</th>
                   <th>Outgoings</th>
                   <th>Gross Profit</th>
                   <th>Per Night</th>
               </tr>
           </thead>
           <tbody>
               ${bookingsRows}
               <tr class="totals-row">
                   <td style="text-align: left;"><strong>Totals</strong></td>
                   <td></td>
                   <td style="text-align: center;"><strong>${totalNights}</strong></td>
                   <td style="text-align: right;"><strong>£${totalAmount.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalCardFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalNetIncome.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalOutgoings.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalGrossProfit.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${avgPerNight.toFixed(2)}</strong></td>
               </tr>
           </tbody>
       </table>
       
       <div class="content-sections">
           <div class="summary-section">
               <div class="summary-title">INCOME & DIRECT EXPENSES</div>
               <table class="summary-table">
                   <tr>
                       <td class="label">TOTAL GUEST PAYMENTS</td>
                       <td class="value">£${calculations.totalRevenue.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">PLATFORM FEES</td>
                       <td class="value">£${calculations.totalPlatformFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CARD PROCESSING FEES</td>
                       <td class="value">£${calculations.totalCardFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CLEANING & LAUNDRY</td>
                       <td class="value">£${calculations.totalOutgoings.toFixed(2)}</td>
                   </tr>
                   <tr class="total-row">
                       <td class="label">GROSS PROFIT</td>
                       <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
                   </tr>
               </table>
           </div>
           
       <div class="summary-section">
         <div class="summary-title">MANAGEMENT FEES & OTHER COSTS</div>
         <table class="summary-table">
             <tr>
                 <td class="label">${managementFeeLabel}</td>
                 <td class="value">£${calculations.managementFees.toFixed(2)}</td>
             </tr>
                   <tr>
                       <td class="label">VAT</td>
                       <td class="value">£${calculations.vat.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CONSUMABLES</td>
                       <td class="value">£${calculations.consumables.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">MAINTENANCE</td>
                       <td class="value">£${calculations.maintenance.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">OTHER EXPENSES</td>
                       <td class="value">£${calculations.otherExpenses.toFixed(2)}</td>
                   </tr>
               </table>
           </div>
       </div>
       
       <div class="settlement-section">
           <div class="settlement-title">SETTLEMENT SUMMARY</div>
           <table class="settlement-table">
               <tr>
                   <td class="label">GROSS PROFIT</td>
                   <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
               </tr>
               <tr>
                   <td class="label">LESS MANAGEMENT FEE & OTHER COSTS</td>
                   <td class="value">£${(calculations.managementFees + calculations.vat + calculations.maintenance + calculations.consumables + calculations.otherExpenses).toFixed(2)}</td>
               </tr>
               <tr class="final-row">
                   <td class="label">SETTLEMENT TO OWNER</td>
                   <td class="value">£${calculations.settlementAmount.toFixed(2)}</td>
               </tr>
           </table>
       </div>
       
       <div class="footer-section">
           <div class="prepared-info">
               <div class="prepared-label">Prepared by</div>
               <div class="prepared-company">Sojourn Executive</div>
           </div>
           
           <div class="company-logo">
               <div class="company-name">SOJOURN</div>
               <div class="company-tagline">EXECUTIVE</div>
           </div>
           
           <div class="footer-meta">
               <div>${new Date().getFullYear()}</div>
               <div>1</div>
           </div>
       </div>
   </div>
</body>
</html>
     `;
},

   // Méthodes helper
// Dans reservationsView.vue, remplacer les méthodes openReportModal() et getPropertySettings() par :

openReportModal(displayName, period) {
  let data, propertyId;
  
  if (period === 'current') {
    data = this.groupedCurrentMonth[displayName];
    propertyId = this.selectedCurrentProperty;
  } else if (period === 'lastmonth') {
    data = this.groupedLastMonth[displayName];
    propertyId = this.selectedLastMonthProperty;
  } else if (period === 'historical') {
    data = this.searchResults;
    propertyId = this.selectedProperty || this.getPropertyIdByName(displayName);
  }
  
  if (!data || data.length === 0) {
    alert('No reservations found for this property and period.');
    return;
  }

  let periodLabel;
  if (period === 'current') {
    periodLabel = 'Current Month';
  } else if (period === 'lastmonth') {
    periodLabel = 'Last Month';
  } else if (period === 'historical') {
    const selectedMonthData = this.availableMonths.find(m => m.value === this.appliedFilters.month);
    periodLabel = selectedMonthData ? selectedMonthData.label : 'Historical Month';
  }

  this.modalData = {
    propertyName: displayName,
    periodLabel: periodLabel,
    reservations: [...data],
    realPropertyId: propertyId
  };

  const savedSettings = this.getPropertySettings(propertyId);
  
  this.modalSettings = {
    managementFeeRate: parseFloat(savedSettings.managementFeeRate) || 16,
    managementFeeBase: savedSettings.managementFeeBase || 'net',
    managementFeeFixedAmount: parseFloat(savedSettings.managementFeeFixedAmount) || 500,
    vatRate: parseFloat(savedSettings.vatRate) || 20,
    maintenance: parseFloat(savedSettings.maintenance) || 0,
    useCustomMaintenance: false,
    customMaintenanceAmount: 0,
    consumables: parseFloat(savedSettings.consumables) || 20,
    otherExpenses: parseFloat(savedSettings.otherExpenses) || 0,
    cleaningMode: savedSettings.cleaningMode || 'rentlio',
    bookingCleaningFee: parseFloat(savedSettings.bookingCleaningFee) || 150,
    otherCleaningFee: parseFloat(savedSettings.otherCleaningFee) || 120,
    cardFeeMode: savedSettings.cardFeeMode || 'rentlio',
    customCardFeeRate: parseFloat(savedSettings.customCardFeeRate) || 2.9,
    customCardFeeFixed: parseFloat(savedSettings.customCardFeeFixed) || 0.30
  };
  
  this.originalSettings = { ...this.modalSettings };
  this.hasUnsavedChanges = false;

  this.calculateMetrics();
  this.generatePreviewHTML();
  this.showReportModal = true;
},

// CORRECTION de getPropertyIdByName() pour garantir la correspondance:
getPropertyIdByName(propertyName) {
  console.log('Searching for property with name:', propertyName);
  console.log('Available properties:', this.properties.map(p => ({ id: p.id, name: p.name })));
  
  const property = this.properties.find(p => p.name === propertyName);
  const propertyId = property ? property.id : null;
  
  console.log('Found property ID:', propertyId, 'for name:', propertyName);
  
  if (!propertyId) {
    console.warn('Property not found! Available names:', this.properties.map(p => p.name));
  }
  
  return propertyId;
},

// CORRECTION de getPropertySettings() pour garantir le bon chargement:
getPropertySettings(propertyId) {
  console.log('Getting settings for property ID:', propertyId);
  
  if (!propertyId) {
    console.log('No property ID, using defaults');
    return this.getDefaultSettings();
  }
  
  const savedSettings = localStorage.getItem('propertySettings');
  console.log('Raw localStorage data:', savedSettings);
  
  if (!savedSettings) {
    console.log('No saved settings found, using defaults');
    return this.getDefaultSettings();
  }
  
  try {
    const allSettings = JSON.parse(savedSettings);
    console.log('All parsed settings:', allSettings);
    
    const propertySettings = allSettings[propertyId];
    console.log('Settings for property', propertyId, ':', propertySettings);
    
    if (!propertySettings) {
      console.log('No settings for this property, using defaults');
      return this.getDefaultSettings();
    }
    
    // Assurer la conversion des types et valeurs par défaut
    const finalSettings = {
      managementFeeRate: parseFloat(propertySettings.managementFeeRate) || 16,
      managementFeeBase: propertySettings.managementFeeBase || 'net',
      vatRate: parseFloat(propertySettings.vatRate) || 20,
      maintenance: parseFloat(propertySettings.maintenance) || 0,
      consumables: parseFloat(propertySettings.consumables) || 20,
      otherExpenses: parseFloat(propertySettings.otherExpenses) || 0,
      cleaningMode: propertySettings.cleaningMode || 'rentlio',
      bookingCleaningFee: parseFloat(propertySettings.bookingCleaningFee) || 150,
      otherCleaningFee: parseFloat(propertySettings.otherCleaningFee) || 120,
      cardFeeMode: propertySettings.cardFeeMode || 'rentlio',
      customCardFeeRate: parseFloat(propertySettings.customCardFeeRate) || 2.9,
      customCardFeeFixed: parseFloat(propertySettings.customCardFeeFixed) || 0.30
    };
    
    console.log('Final processed settings:', finalSettings);
    return finalSettings;
    
  } catch (e) {
    console.error('Error parsing settings:', e);
    return this.getDefaultSettings();
  }
},

   getDefaultSettings() {
     return {
       managementFeeRate: 16.0,
       managementFeeBase: 'net',
       vatRate: 20.0,
       maintenance: 0.0,
       consumables: 20.0,
       otherExpenses: 0.0,
       cleaningMode: 'rentlio',
       bookingCleaningFee: 150.0,
       otherCleaningFee: 120.0,
       cardFeeMode: 'rentlio',
       customCardFeeRate: 2.9,
       customCardFeeFixed: 0.00
     };
   },

   // Méthodes Excel existantes
   downloadRapportPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateRapportExcel(data, `${unitName}_${period}_rapport`, unitName);
   },

   generateRapportExcel(reservations, filename, propertyName) {
     const propertyId = this.getPropertyIdByName(propertyName);
     const propertySettings = this.getPropertySettings(propertyId);
     
     const data = reservations.map(item => {
       const amount = parseFloat(item.totalReservationPrice || 0);
       const fees = parseFloat(item.channelCommissionNominal || 0);
       
       let cardFee = 0;
       if (item.otaChannelName == 'Booking.com') {
         if (propertySettings.cardFeeMode === 'rentlio') {
           const totalWithFees = parseFloat(item.totalCost || item.totalReservationPrice || 0);
           const basePrice = parseFloat(item.accommodationCost || 0);
           const vatAmount = parseFloat(item.vatAmount || 0);
           cardFee = Math.max(0, totalWithFees - basePrice - vatAmount);
         } else {
           cardFee = (amount * propertySettings.customCardFeeRate / 100);
         }
       }
       
       let outgoings = 0;
       const isExtension = (item.guestName || '').toLowerCase().includes('extension');

       if (isExtension) {
         outgoings = 0;
       } else {
         if (propertySettings.cleaningMode === 'rentlio') {
           outgoings = parseFloat(item.totalServicesPrice || 0);
         } else {
           const platform = item.otaChannelName || '';
           if (platform === 'Booking.com') {
             outgoings = propertySettings.bookingCleaningFee || 150;
           } else {
             outgoings = propertySettings.otherCleaningFee || 120;
           }
         }
       }
       
       const netIncome = amount - fees - cardFee;
       const grossProfit = netIncome - outgoings;
       const perNight = item.totalNights > 0 ? amount / item.totalNights : 0;

       return [
         item.guestName || '',
         item.otaChannelName || 'Direct',
         item.totalNights || 0,
         amount.toFixed(2),
         fees.toFixed(2),
         cardFee.toFixed(2),
         netIncome.toFixed(2),
         outgoings.toFixed(2),
         grossProfit.toFixed(2),
         perNight.toFixed(2)
       ];
     });

     const totalNights = data.reduce((sum, r) => sum + r[2], 0);
     const totalAmount = data.reduce((sum, r) => sum + parseFloat(r[3]), 0);
     const correctPerNight = data.length > 0 ? (data.reduce((sum, r) => sum + parseFloat(r[9]), 0) / data.length).toFixed(2) : '0.00';

     const totals = [
       'Totals',
       '',
       totalNights,
       totalAmount.toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[4]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[5]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[6]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[7]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[8]), 0).toFixed(2),
       correctPerNight
     ];

     const totalRevenue = parseFloat(totals[3]);
     const totalCardFees = parseFloat(totals[5]);
     const netIncomeTotal = parseFloat(totals[6]);
     const grossProfitTotal = parseFloat(totals[8]);
     
     let managementFee = 0;
     let managementFeeLabel = '';
     
     if (propertySettings.managementFeeBase === 'revenue') {
      managementFee = (totalRevenue * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Total Revenue)`;
    } else if (propertySettings.managementFeeBase === 'gross') {
      managementFee = (grossProfitTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Gross Profit)`;
    } else {
      managementFee = (netIncomeTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Net Income)`;
    }

     const vat = (managementFee * propertySettings.vatRate) / 100;
     const maintenance = propertySettings.maintenance;
     const consumables = propertySettings.consumables;
     const otherExpenses = propertySettings.otherExpenses;
     const toClient = grossProfitTotal - managementFee - vat - maintenance - consumables - otherExpenses;

     const ws = XLSX.utils.aoa_to_sheet([
       [propertyName || 'Property Report'],
       [''],
       ['Booking Name', 'Source', 'No. Nights', 'Amount', 'Fees', 'Card Fee', 'Net Income', 'Outgoings', 'Gross Profit', 'Per Night'],
       ...data,
       totals,
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'Profit', '', grossProfitTotal.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Days in month', '', 31, '', '', '', '', managementFeeLabel, '', managementFee.toFixed(2)],
       ['Occupancy', '', Math.round((totals[2] / 31) * 100) + '%', '', '', '', '', 'VAT', '', vat.toFixed(2)],
       ['', '', '', '', '', '', '', 'Maintenance', '', maintenance.toFixed(2)],
       ['', '', '', '', '', '', '', 'Consumables', '', consumables.toFixed(2)],
       ['', '', '', '', '', '', '', 'Other Expenses', '', otherExpenses.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'To Client', '', toClient.toFixed(2)]
     ]);

     ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, propertyName || 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   downloadPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateExcelReport(data, `${unitName}_${period}_report`);
   },
   
   generateExcelReport(reservations, filename) {
     const data = reservations.map(item => ({
       'Booking Name': item.guestName,
       'Source': item.otaChannelName || 'Direct',
       'No. Nights': item.totalNights,
       'Amount': parseFloat(item.totalReservationPrice).toFixed(2),
       'Fees': parseFloat(item.channelCommissionNominal || 0).toFixed(2),
       'Card Fee': parseFloat(item.cardFee || 0).toFixed(2),
       'Gross Income': parseFloat(item.totalReservationPrice).toFixed(2),
       'Outgoings': parseFloat(item.totalServicesPrice).toFixed(2),
       'Net Income': parseFloat(item.accommodationCost).toFixed(2),
       'Per Night': parseFloat(item.pricePerNight).toFixed(2),
       'Arrival Date': this.formatDate(item.arrivalDate),
       'Departure Date': this.formatDate(item.departureDate),
     }));

     const ws = XLSX.utils.json_to_sheet(data);
    
     const totalNightsExcel = reservations.reduce((sum, r) => sum + r.totalNights, 0);
     const totalAmountExcel = reservations.reduce((sum, r) => sum + parseFloat(r.totalReservationPrice), 0);
     const correctPerNightExcel = reservations.length > 0 ? (reservations.reduce((sum, r) => sum + parseFloat(r.pricePerNight), 0) / reservations.length).toFixed(2) : '0.00';

     XLSX.utils.sheet_add_aoa(ws, [
       ["", "", "", "", "", "", "", "", "", "", ""],
       ["Totals", "", 
        totalNightsExcel,
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.channelCommissionNominal || 0), 0).toFixed(2),
        "",
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.totalServicesPrice), 0).toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.accommodationCost), 0).toFixed(2),
        correctPerNightExcel
       ]
     ], { origin: -1 });

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   // Méthodes de données existantes
   async fetchData() {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     const headers = { 'Authorization': `Bearer ${token}` };

     try {
       const propertiesRes = await axios.get(`${this.baseUrl}/properties`, { headers });
       this.properties = propertiesRes.data.properties.data;

       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/reports/current?propertyId=${this.selectedCurrentProperty}`, { headers });
       this.currentMonthReservations = Object.values(currentRes.data.reservations);
       this.loading = false;

       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/reports/lastmonth?propertyId=${this.selectedLastMonthProperty}`, { headers });
       this.lastMonthReservations = Object.values(lastMonthRes.data.reservations);
       this.loadingLastMonth = false;

     } catch (error) {
       console.error('Error fetching data:', error);
       if (error.response && error.response.status === 401) {
         localStorage.removeItem('token');
         this.$router.push('/login');
       }
     }
   },

   async onCurrentPropertyChange() {
     const token = localStorage.getItem('token');
     if (!token) return;

     const headers = { 'Authorization': `Bearer ${token}` };
     
     try {
       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/reports/current?propertyId=${this.selectedCurrentProperty}`, { headers });
       this.currentMonthReservations = Object.values(currentRes.data.reservations);
       this.loading = false;
     } catch (error) {
       console.error('Error fetching current month data:', error);
       this.loading = false;
     }
   },

   async onLastMonthPropertyChange() {
     const token = localStorage.getItem('token');
     if (!token) return;

     const headers = { 'Authorization': `Bearer ${token}` };
     
     try {
       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/reports/lastmonth?propertyId=${this.selectedLastMonthProperty}`, { headers });
       this.lastMonthReservations = Object.values(lastMonthRes.data.reservations);
       this.loadingLastMonth = false;
     } catch (error) {
       console.error('Error fetching last month data:', error);
       this.loadingLastMonth = false;
     }
   },

   async applyFilters() {
     console.log('Applying filters...');
     
     if (!this.selectedMonth) {
       alert('Please select a month');
       return;
     }
     
     if (!this.selectedProperty) {
       alert('Please select a property');
       return;
     }
     
     this.appliedFilters = {
       property: this.selectedProperty,
       month: this.selectedMonth
     };
     
     this.loadingHistory = true;
     this.searchResults = [];
     
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     const headers = { 'Authorization': `Bearer ${token}` };
     
     try {
       console.log(`Fetching reservations for month: ${this.selectedMonth}, property: ${this.selectedProperty}`);
       
       // Trouver le type de mois sélectionné
       const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
       const monthType = selectedMonthData ? selectedMonthData.type : 'historical';
       
       let response;
       let url;
       
       if (monthType === 'current') {
         // Utiliser l'endpoint des rapports courants
         console.log('Using current month report endpoint');
         url = `${this.baseUrl}/reports/current?propertyId=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else if (monthType === 'lastmonth') {
         // Utiliser l'endpoint des rapports du mois précédent
         console.log('Using last month report endpoint');
         url = `${this.baseUrl}/reports/lastmonth?propertyId=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else {
         // Pour les mois historiques, calculer les dates de début et fin du mois
         console.log('Using generic reservations endpoint for historical month');
         const year = parseInt(this.selectedMonth.substring(0, 4));
         const month = parseInt(this.selectedMonth.substring(5, 7)) - 1; // JavaScript months are 0-indexed
         
         const firstDay = new Date(year, month, 1);
         const lastDay = new Date(year, month + 1, 0);
         
         const dateFrom = firstDay.toISOString().substring(0, 10);
         const dateTo = lastDay.toISOString().substring(0, 10);
         
         url = `${this.baseUrl}/reservations?dateFrom=${dateFrom}&dateTo=${dateTo}&perPage=200&order_by=arrivalDate&order_direction=ASC&propertyId=${this.selectedProperty}`;
         
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations?.data || [];
       }
       
       this.currentPage = 1;
       console.log(`Final results: ${this.searchResults.length} reservations`);
       console.log('Used URL:', url);
       
     } catch (error) {
       console.error('Error fetching reservations:', error);
       alert('Error fetching reservations. Please try again.');
     } finally {
       this.loadingHistory = false;
     }
   },

   clearFilters() {
     this.selectedProperty = '';
     this.selectedMonth = '';
     this.appliedFilters = {
       property: '',
       month: ''
     };
     this.searchResults = [];
     this.currentPage = 1;
     this.loadingHistory = false;
   },

   prevPage() {
     if (this.currentPage > 1) {
       this.currentPage--;
     }
   },
   
   nextPage() {
     if (this.currentPage < this.totalPages) {
       this.currentPage++;
     }
   },
   
 handleNumericInput(fieldName, event) {
    const value = event.target.value;
    
    // Si le champ est complètement vide, mettre 0
    if (value === '' || value === null || value === undefined) {
      this.modalSettings[fieldName] = 0;
      return;
    }
    
    // Convertir en nombre et s'assurer que c'est valide
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      this.modalSettings[fieldName] = 0;
    } else {
      this.modalSettings[fieldName] = numValue;
    }
  },
 

ensureMinimumValue(fieldName, minimumValue = 0) {
    // Vérifier quand l'utilisateur quitte le champ
    const currentValue = this.modalSettings[fieldName];
    
    if (currentValue === null || currentValue === undefined || currentValue === '' || isNaN(currentValue) || currentValue < minimumValue) {
      this.modalSettings[fieldName] = minimumValue;
      this.markAsModified();
      this.calculateMetrics();
    }
  
},

 // Contacts management methods
 loadContacts() {
   const savedContacts = localStorage.getItem('emailContacts');
   this.contacts = savedContacts ? JSON.parse(savedContacts) : [];
   this.cleanInvalidContacts();
 },

 cleanInvalidContacts() {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   const initialCount = this.contacts.length;
   
   this.contacts = this.contacts.filter(contact => {
     const isValid = contact.email && emailRegex.test(contact.email);
     if (!isValid) {
       console.warn('Removing invalid contact:', contact);
     }
     return isValid;
   });
   
   // Sauvegarder si des contacts ont été supprimés
   if (this.contacts.length < initialCount) {
     this.saveContacts();
     console.log(`Cleaned ${initialCount - this.contacts.length} invalid contact(s)`);
   }
 },

 saveContacts() {
   localStorage.setItem('emailContacts', JSON.stringify(this.contacts));
 },

 addContact() {
   if (!this.newContact.name || !this.newContact.email) {
     alert('Please fill in both name and email');
     return;
   }

   // Validation du format d'email
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   if (!emailRegex.test(this.newContact.email)) {
     alert('Please enter a valid email address (e.g., user@domain.com)');
     return;
   }

   // Vérifier si l'email existe déjà
   const existingContact = this.contacts.find(c => c.email.toLowerCase() === this.newContact.email.toLowerCase());
   if (existingContact) {
     alert('This email address already exists in your contacts');
     return;
   }

   const contact = {
     id: Date.now(),
     name: this.newContact.name,
     email: this.newContact.email,
     createdAt: new Date().toISOString()
   };

   this.contacts.push(contact);
   this.saveContacts();
   this.newContact = { name: '', email: '' };
   
   // Afficher un message de succès
   alert(`Contact "${contact.name}" added successfully!`);
 },

 editContact(contact) {
   this.editingContact = { ...contact };
 },

 saveEditContact() {
   const index = this.contacts.findIndex(c => c.id === this.editingContact.id);
   if (index !== -1) {
     this.contacts[index] = { ...this.editingContact };
     this.saveContacts();
     this.editingContact = null;
   }
 },

 deleteContact(contactId) {
   if (confirm('Are you sure you want to delete this contact?')) {
     this.contacts = this.contacts.filter(c => c.id !== contactId);
     this.saveContacts();
   }
 },

 openContactsModal() {
   this.showContactsModal = true;
 },

 closeContactsModal() {
   this.showContactsModal = false;
   this.editingContact = null;
   this.newContact = { name: '', email: '' };
 },

 // Méthodes pour gérer le dernier contact sélectionné
 getLastSelectedContact() {
   const lastContactId = localStorage.getItem('lastSelectedContactId');
   if (lastContactId) {
     return this.contacts.find(c => c.id.toString() === lastContactId);
   }
   return null;
 },

 setLastSelectedContact(contact) {
   if (contact && contact.id) {
     localStorage.setItem('lastSelectedContactId', contact.id.toString());
   }
 },

 
 formatContactDate(dateString) {
   const date = new Date(dateString);
   return date.toLocaleDateString('en-US', { 
     month: 'short', 
     day: 'numeric',
     year: 'numeric'
   });
 },

 async sendReportByEmail() {
   if (!this.emailData.contact) {
     alert('Please select a contact');
     return;
   }

   this.setLastSelectedContact(this.emailData.contact);
   this.sendingEmail = true;
   
   // Afficher immédiatement le popup de confirmation
   this.displayEmailSentPopup();
   
   try {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Préparer le message du rapport mensuel en anglais
     const message = `
       <h2>Dear ${this.emailData.contact.name},</h2>
       <p>Please find attached your monthly property report for <strong>${this.emailData.propertyName}</strong> for the period <strong>${this.emailData.period}</strong>.</p>
       
       <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
         <h3 style="margin: 0 0 10px 0; color: #667eea;">📊 Report Summary</h3>
         <p style="margin: 5px 0;"><strong>Property:</strong> ${this.emailData.propertyName}</p>
         <p style="margin: 5px 0;"><strong>Period:</strong> ${this.emailData.period}</p>
         <p style="margin: 5px 0;"><strong>Report Type:</strong> ${this.emailData.reportType}</p>
       </div>
       
       <p>The report includes detailed information about:</p>
       <ul>
         <li>Booking overview and revenue analysis</li>
         <li>Income and direct expenses breakdown</li>
         <li>Management fees and operational costs</li>
         <li>Settlement summary</li>
       </ul>
       
       <p style="margin-top: 20px;">If you have any questions about this report, please don't hesitate to contact us.</p>
       
       <p>Best regards,<br>
       <strong>Sojourn Executive Team</strong></p>
     `;

     const emailPayload = {
       to: this.emailData.contact.email,
       subject: `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`,
       message: message,
       fromName: "Sojourn Reports"
     };

     // Ajouter des données de rapport si PDF sélectionné
     if (this.emailOptions.includePDF) {
       try {
         console.log('Preparing report data for backend PDF generation...');
         
         // Récupérer les données du rapport depuis les variables existantes
         const reportData = this.generateReportData();
         
         // Ajouter les données de rapport au payload
         emailPayload.reportData = reportData;
         
         console.log('Report data prepared for backend:', reportData);
         
       } catch (error) {
         console.error('Error preparing report data:', error);
         alert('Error preparing report data. Sending email without attachment.');
       }
     }
     
     // Note: Excel option removed - backend only supports PDF generation

     // Ajouter message personnel si fourni
     if (this.emailOptions.message && this.emailOptions.message.trim()) {
       emailPayload.message = `
         <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
           <h4 style="margin: 0 0 10px 0; color: #2c5282;">Personal Message:</h4>
           <p style="margin: 0; font-style: italic;">${this.emailOptions.message}</p>
         </div>
         ${emailPayload.message}
       `;
     }

     // Validation de la taille de la payload
    const payloadSize = JSON.stringify(emailPayload).length;
    console.log('Sending email with payload size:', payloadSize, 'bytes');
    console.log('Payload structure:', {
      to: emailPayload.to,
      subject: emailPayload.subject,
      messageLength: emailPayload.message?.length,
      attachmentsCount: emailPayload.attachments?.length || 0,
      attachmentsSizes: emailPayload.attachments?.map(att => ({ name: att.name, size: att.content?.length })) || []
    });
    
    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPayload.to || !emailRegex.test(emailPayload.to)) {
      throw new Error(`Invalid email address: "${emailPayload.to}"`);
    }
    console.log('Email validation passed for:', emailPayload.to);
    
    // Temporairement désactivé pour diagnostic
    // if (payloadSize > 10 * 1024 * 1024) { // 10MB limit
    //   console.error('Payload too large:', payloadSize, 'bytes');
    //   throw new Error(`Email payload too large (${Math.round(payloadSize / 1024 / 1024)}MB). Please reduce attachment size.`);
    // }

     try {
       // Vérifier le token avant l'envoi
       if (!token) {
         throw new Error('Authentication token is missing');
       }
       
       console.log('Token present:', !!token, 'Token length:', token?.length);
       console.log('Making API call to:', `${this.baseUrl}/send-email`);
       
       const response = await axios.post(`${this.baseUrl}/send-email`, emailPayload, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         },
         timeout: 480000, // 8 minutes timeout pour les gros fichiers
         maxContentLength: 50 * 1024 * 1024, // 50MB max
         validateStatus: function (status) {
           return status < 600; // Ne pas rejeter les erreurs 4xx et 5xx pour pouvoir les analyser
         }
       });

       console.log('API Response status:', response.status);
       console.log('API Response headers:', response.headers);
       console.log('API Response data:', response.data);

       if (response.status === 200 && response.data && response.data.status === 'success') {
         // alert(`✅ Report successfully sent to ${this.emailData.contact.name} (${this.emailData.contact.email})`);
         // Pas d'alert car le popup s'affiche déjà
         // this.closeEmailModal(); // Déjà fermé dans displayEmailSentPopup()
       } else {
         // Gestion détaillée des erreurs
         let errorMessage = 'Email sending failed';
         
         if (response.status === 500) {
           errorMessage = `Server error (500): ${response.data?.message || response.data || 'Internal server error'}`;
         } else if (response.status === 401) {
           errorMessage = 'Authentication failed. Please login again.';
         } else if (response.status === 413) {
           errorMessage = 'Request too large. Please reduce attachment size.';
         } else if (response.data?.message) {
           errorMessage = response.data.message;
         }
         
         throw new Error(errorMessage);
       }
     } catch (apiError) {
       console.error('=== API ERROR DETAILS ===');
       console.error('Error type:', apiError.constructor.name);
       console.error('Error message:', apiError.message);
       console.error('Error code:', apiError.code);
       console.error('Response status:', apiError.response?.status);
       console.error('Response headers:', apiError.response?.headers);
       console.error('Response data:', apiError.response?.data);
       console.error('Request config:', {
         url: apiError.config?.url,
         method: apiError.config?.method,
         headers: apiError.config?.headers,
         timeout: apiError.config?.timeout
       });
       console.error('========================');
       
       // Gestion spécifique des erreurs courantes
       if (apiError.code === 'ECONNABORTED') {
         throw new Error('Request timeout. The server took too long to respond.');
       } else if (apiError.code === 'NETWORK_ERROR') {
         throw new Error('Network error. Please check your internet connection.');
       } else if (apiError.response?.status === 500) {
         const serverError = apiError.response?.data || 'Unknown server error';
         throw new Error(`Server error: ${typeof serverError === 'string' ? serverError : JSON.stringify(serverError)}`);
       }
       
       throw apiError;
     } finally {
       this.sendingEmail = false;
     }
   } catch (error) {
     console.error('Error sending email:', error);
     this.sendingEmail = false;
     let errorMessage = 'Failed to send email. Please try again.';
     
     if (error.response?.data?.message) {
       errorMessage = error.response.data.message;
     } else if (error.message) {
       errorMessage = error.message;
     }
     
     // alert(`❌ Error: ${errorMessage}`);
     console.error('Email sending error:', errorMessage);
   } finally {
     this.sendingEmail = false;
   }
 },

 // Méthodes pour Email Modal avec contacts
 openEmailModal(reportType, propertyName, period) {
   // Recharger les contacts depuis localStorage au cas où ils auraient été modifiés
   this.loadContacts();
   
   // Essayer de pré-sélectionner le dernier contact utilisé
   const lastContact = this.getLastSelectedContact();
   
   // Configurer les données du modal email
   this.emailData = {
     contact: lastContact,
     reportType: reportType,
     propertyName: propertyName,
     period: period
   };
   
   // Réinitialiser les options email
   this.emailOptions = {
     includeExcel: false,
     includePDF: true,
     message: ''
   };
   
   // Ouvrir le modal
   this.showEmailModal = true;
 },

 closeEmailModal() {
   this.showEmailModal = false;
   this.emailData = {
     contact: null,
     reportType: '',
     propertyName: '',
     period: ''
   };
 },

 displayEmailSentPopup() {
   // Fermer le modal d'email
   this.showEmailModal = false;
   
   // Afficher le popup de confirmation
   this.showEmailSentPopup = true;
   
   // Fermer automatiquement après 8 secondes
   setTimeout(() => {
     this.showEmailSentPopup = false;
   }, 8000);
 },

 closeEmailSentPopup() {
   this.showEmailSentPopup = false;
 },

 async generatePDFForEmail(propertyName, period) {
   try {
     // Utiliser la même logique que downloadPDF mais créer un File object
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     // Utiliser EXACTEMENT la même logique que downloadPDF()
     const iframe = document.createElement('iframe');
     iframe.style.cssText = `
       position: absolute !important;
       top: -10000px !important;
       left: -10000px !important;
       width: 210mm !important;
       height: 297mm !important;
       border: none !important;
       opacity: 0 !important;
       pointer-events: none !important;
     `;
     
     document.body.appendChild(iframe);
     
     // Utiliser la même fonction que downloadPDF()
     const pdfHTML = this.generatePDFHTML();
     iframe.contentDocument.open();
     iframe.contentDocument.write(pdfHTML);
     iframe.contentDocument.close();
     
     await new Promise(resolve => {
       iframe.onload = resolve;
       setTimeout(resolve, 500);
     });

     const canvas = await html2canvas(iframe.contentDocument.body, {
       scale: 1,           // Réduit de 2 à 1 pour réduire la taille
       useCORS: true,
       backgroundColor: '#ffffff',
       width: 794,
       height: 1123,
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/jpeg', 0.7);  // JPEG avec compression 70%
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     document.body.removeChild(iframe);
     
     // Retourner le blob PDF pour l'email (même PDF que downloadPDF())
     return pdf.output('blob');

   } catch (error) {
     console.error('Error generating PDF for email:', error);
     throw error;
   }
 },

 // Nouvelle méthode pour envoyer l'email depuis le modal avec le PDF déjà généré
 sendReportByEmailFromModal() {
   // Ouvrir le popup d'envoi d'email mais avec le PDF du modal
   this.openEmailModal('Report', this.modalData.propertyName, this.modalData.periodLabel);
 },

 // Générer le PDF à partir du contenu actuel du modal (même que downloadPDF)
 async generatePDFFromModal() {
   try {
     // Utiliser exactement la même logique que downloadPDF mais retourner un blob
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     const previewContent = document.querySelector('.pdf-preview-wrapper');
     if (!previewContent) {
       throw new Error('Preview content not found');
     }

     const canvas = await html2canvas(previewContent, {
       scale: 2,
       useCORS: true,
       backgroundColor: '#ffffff',
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/png');
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     return pdf.output('blob');
   } catch (error) {
     console.error('Error generating PDF from modal:', error);
     throw error;
   }
 },

 // Nouvelle fonction pour préparer les données de rapport pour le backend
 generateReportData() {
   try {
     // Utiliser les données du modal actuel si disponible
     if (this.modalData && this.modalData.reservations) {
       const reservations = this.modalData.reservations;
       const propertyName = this.modalData.propertyName;
       const period = this.modalData.periodLabel;
       
       // Calculer les totaux
       const totalGuestPayments = reservations.reduce((sum, res) => sum + (res.totalReservationPrice || 0), 0);
       const platformFees = reservations.reduce((sum, res) => sum + (res.channelCommissionNominal || 0), 0);
       const cardFees = reservations.reduce((sum, res) => sum + (res.cardFee || 0), 0);
       const cleaningFees = reservations.reduce((sum, res) => sum + (res.totalServicesPrice || 0), 0);
       const grossProfit = totalGuestPayments - platformFees - cardFees - cleaningFees;
       
       // Calculer les frais de gestion (20%)
       const managementFees = grossProfit * 0.20;
       const vat = managementFees * 0.20;
       
       // Préparer les données des réservations
       const bookings = reservations.map(res => ({
         guestName: res.guestName || 'Unknown Guest',
         otaChannelName: res.otaChannelName || 'Direct',
         totalNights: res.totalNights || 0,
         totalAmount: res.totalReservationPrice || 0,
         channelCommission: res.channelCommissionNominal || 0,
         cardFee: res.cardFee || 0,
         netIncome: (res.totalReservationPrice || 0) - (res.channelCommissionNominal || 0) - (res.cardFee || 0),
         totalServicesPrice: res.totalServicesPrice || 0,
         grossProfit: (res.totalReservationPrice || 0) - (res.channelCommissionNominal || 0) - (res.cardFee || 0) - (res.totalServicesPrice || 0),
         pricePerNight: res.pricePerNight || 0,
         netPerNight: res.totalNights > 0 ? ((res.totalReservationPrice || 0) - (res.channelCommissionNominal || 0) - (res.cardFee || 0)) / res.totalNights : 0
       }));
       
       // Calculer les totaux des colonnes
       const totalNights = bookings.reduce((sum, b) => sum + b.totalNights, 0);
       const totalAmount = bookings.reduce((sum, b) => sum + b.totalAmount, 0);
       const totalFees = bookings.reduce((sum, b) => sum + b.channelCommission, 0);
       const totalCardFees = bookings.reduce((sum, b) => sum + b.cardFee, 0);
       const totalNetIncome = bookings.reduce((sum, b) => sum + b.netIncome, 0);
       const totalOutgoings = bookings.reduce((sum, b) => sum + b.totalServicesPrice, 0);
       const totalGrossProfitBookings = bookings.reduce((sum, b) => sum + b.grossProfit, 0);
       
       // Frais fixes (à adapter selon vos besoins)
       const consumables = 150;
       const maintenance = 200;
       const otherExpenses = 50;
       const totalDeductions = managementFees + vat + consumables + maintenance + otherExpenses;
       const settlementAmount = grossProfit - totalDeductions;
       
       return {
         propertyName: propertyName,
         reportPeriod: period,
         totalGuestPayments: totalGuestPayments,
         platformFees: platformFees,
         cardFees: cardFees,
         cleaningFees: cleaningFees,
         grossProfit: grossProfit,
         managementFeeRate: 20,
         managementFees: managementFees,
         vat: vat,
         consumables: consumables,
         maintenance: maintenance,
         otherExpenses: otherExpenses,
         bookings: bookings,
         totalNights: totalNights,
         totalAmount: totalAmount,
         totalFees: totalFees,
         totalCardFees: totalCardFees,
         totalNetIncome: totalNetIncome,
         totalOutgoings: totalOutgoings,
         totalGrossProfit: totalGrossProfitBookings,
         avgPerNight: totalNights > 0 ? totalAmount / totalNights : 0,
         avgNetPerNight: totalNights > 0 ? totalNetIncome / totalNights : 0,
         totalDeductions: totalDeductions,
         settlementAmount: settlementAmount,
         currentYear: new Date().getFullYear()
       };
     } else {
       throw new Error('No modal data available for report generation');
     }
   } catch (error) {
     console.error('Error generating report data:', error);
     throw error;
   }
 },

 async prepareReportData(propertyName, period) {
   // Simuler la préparation des données - ajuster selon la logique existante
   const token = localStorage.getItem('token');
   const headers = { 'Authorization': `Bearer ${token}` };
   
   // Récupérer les données selon la période
   let reservations = [];
   if (period === 'Current Month') {
     const response = await axios.get(`${this.baseUrl}/reports/current`, { headers });
     reservations = Object.values(response.data.reservations);
   } else if (period === 'Last Month') {
     const response = await axios.get(`${this.baseUrl}/reports/lastmonth`, { headers });
     reservations = Object.values(response.data.reservations);
   }

   return {
     propertyName,
     periodLabel: period,
     reservations
   };
 },

 generatePDFHTMLForProperty(reportData) {
   // Utiliser la même logique que generatePDFHTML mais avec les données spécifiques
   // Pour simplifier, on retourne un HTML basique - à améliorer selon le besoin
   return `
     <!DOCTYPE html>
     <html>
     <head>
       <title>Report - ${reportData.propertyName}</title>
       <style>
         body { font-family: Arial, sans-serif; padding: 20px; }
         .header { text-align: center; margin-bottom: 30px; }
         .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
         .subtitle { font-size: 18px; color: #666; }
       </style>
     </head>
     <body>
       <div class="header">
         <div class="title">Monthly Report</div>
         <div class="subtitle">${reportData.propertyName}</div>
         <div class="subtitle">${reportData.periodLabel}</div>
       </div>
       <div class="content">
         <p>Number of reservations: ${reportData.reservations.length}</p>
         <p>Report generated on: ${new Date().toLocaleDateString('en-US')}</p>
       </div>
     </body>
     </html>
   `;
 },

 closeEmailSender() {
   this.showEmailSender = false;
   this.currentPDFFile = null;
   this.currentReportData = {};
 }
 },

 mounted() {
   this.loadContacts();
   this.fetchData();
   
   // Check if should open contacts modal from URL parameter
   if (this.$route.query.openContacts === 'true') {
     this.openContactsModal();
   }
 }
};

</script>

<style scoped>
/* Import all dashboard styles */
.dashboard-container {
 display: flex;
 min-height: 100vh;
 background: #f8fafc;
}

/* Styles pour les segments de réservations */
.segment-row {
  background: #f8fafc !important;
  border-left: 3px solid #667eea;
}

.segment-row:hover {
  background: #f1f5f9 !important;
}

.segment-badge {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: help;
}

.segment-badge:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: scale(1.05);
}

/* Sidebar Styles */
.sidebar {
 width: 280px;
 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
 color: white;
 transition: width 0.3s ease;
 position: fixed;
 height: 100vh;
 z-index: 1000;
 box-shadow: 2px 0 10px rgba(0,0,0,0.1);
}

.sidebar-collapsed {
 width: 70px;
}

.sidebar-header {
 padding: 20px;
 border-bottom: 1px solid rgba(255,255,255,0.1);
}

.toggle-btn {
 background: none;
 border: none;
 color: white;
 font-size: 20px;
 cursor: pointer;
 margin-bottom: 15px;
}

.sidebar-title {
 font-size: 18px;
 font-weight: 600;
 margin: 0;
}

.sidebar-nav {
 padding: 20px 0;
}

.nav-item {
 display: flex;
 align-items: center;
 padding: 15px 20px;
 color: rgba(255,255,255,0.8);
 text-decoration: none;
 transition: all 0.3s ease;
 margin: 0 10px;
 border-radius: 10px;
}

.nav-item:hover,
.nav-item.active {
 background: rgba(255,255,255,0.1);
 color: white;
 transform: translateX(5px);
}

.nav-icon {
 font-size: 18px;
 margin-right: 15px;
 width: 20px;
}

.nav-text {
 font-weight: 500;
}

/* Main Content */
.main-content {
 flex: 1;
 margin-left: 280px;
 padding: 30px;
 transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
 margin-left: 70px;
}

.content-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 30px;
}

.content-header h1 {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0;
}

.user-info {
 display: flex;
 align-items: center;
 gap: 15px;
}

.logout-btn {
 background: #e53e3e;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.logout-btn:hover {
 background: #c53030;
}

/* Section Cards */
.section-card {
 background: white;
 border-radius: 16px;
 box-shadow: 0 4px 25px rgba(0,0,0,0.08);
 margin-bottom: 30px;
 overflow: hidden;
 border: 1px solid #e2e8f0;
}

.section-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
 margin: 0;
 font-size: 20px;
 font-weight: 600;
 color: #1a202c;
}

.section-title-container {
 display: flex;
 flex-direction: column;
 gap: 10px;
}

.property-selector {
 display: flex;
 align-items: center;
 gap: 8px;
}

.property-selector label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 min-width: 60px;
}

.property-select-small {
 padding: 6px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 13px;
 font-weight: 500;
 min-width: 150px;
 transition: border-color 0.3s ease;
 background: white;
}

.properties-section {
 padding: 0;
}

.property-group {
 border-bottom: 1px solid #e2e8f0;
}

.property-group:last-child {
 border-bottom: none;
}

.property-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 20px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
}

.property-header h3 {
 margin: 0;
 font-size: 18px;
 font-weight: 600;
 color: #2b6cb0;
}

.property-stats {
 display: flex;
 gap: 20px;
 align-items: center;
}

.stat {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

.stat.revenue {
 font-weight: 700;
 color: #22543d;
 font-size: 16px;
}

.property-actions {
 display: flex;
 gap: 8px;
}

/* Buttons */
.btn {
 display: inline-flex;
 align-items: center;
 justify-content: center;
 padding: 10px 20px;
 border-radius: 8px;
 text-decoration: none;
 font-weight: 600;
 font-size: 14px;
 transition: all 0.3s ease;
 border: 2px solid transparent;
 cursor: pointer;
 min-width: 120px;
}

.btn:disabled {
 opacity: 0.6;
 cursor: not-allowed;
}

.btn-sm {
 padding: 6px 12px;
 font-size: 12px;
 min-width: 70px;
}

.btn-excel {
 background-color: #80CBC4;
 color: white;
 border: 1px solid #80CBC4;
}

.btn-excel:hover:not(:disabled) {
 background-color: #6bb3ad;
 border-color: #6bb3ad;
 transform: translateY(-1px);
}

.btn-rapport-excel {
 background-color: #FBF8EF;
 color: #333;
 border: 1px solid #d6d2c4;
}

.btn-rapport-excel:hover:not(:disabled) {
 background-color: #f0ece3;
 border-color: #c9c4b7;
 transform: translateY(-1px);
}

.btn-report {
 background-color: #667eea;
 color: white;
 border: 1px solid #667eea;
}

.btn-report:hover:not(:disabled) {
 background-color: #5a67d8;
 border-color: #5a67d8;
 transform: translateY(-1px);
}

.btn-search {
 background-color: #48BB78;
 color: white;
 border: 1px solid #48BB78;
}

.btn-search:hover:not(:disabled) {
 background-color: #38A169;
 border-color: #38A169;
 transform: translateY(-1px);
}

.btn-outline {
 background: transparent;
 color: #667eea;
 border-color: #667eea;
}

.btn-outline:hover:not(:disabled) {
 background: #667eea;
 color: white;
}

.btn-primary {
 background: #667eea;
 color: white;
 border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
 background: #5a67d8;
 transform: translateY(-1px);
}

.btn-pdf {
 background-color: #FFB433;
 color: white;
 border: 1px solid #FFB433;
}

.btn-pdf:hover:not(:disabled) {
 background-color: #e5a02b;
 border-color: #e5a02b;
 transform: translateY(-1px);
}

/* Table Styles */
.table-container {
 overflow-x: auto;
}

.data-table {
 width: 100%;
 border-collapse: collapse;
}

.data-table th {
 background: #f8fafc;
 padding: 16px 20px;
 text-align: left;
 font-weight: 600;
 color: #4a5568;
 font-size: 13px;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 border-bottom: 2px solid #e2e8f0;
}

.data-table td {
 padding: 16px 20px;
 border-bottom: 1px solid #e2e8f0;
 color: #2d3748;
 vertical-align: middle;
}

.data-table tbody tr:hover {
 background: #f7fafc;
}

.property-name {
 font-weight: 600;
 color: #2b6cb0;
}

.guest-name {
 font-weight: 500;
 color: #2d3748;
}

.amount {
 text-align: right;
 font-weight: 600;
 color: #22543d;
}

.text-center {
 text-align: center;
}

.source-badge {
 display: inline-block;
 padding: 4px 8px;
 background: #e2e8f0;
 color: #4a5568;
 border-radius: 12px;
 font-size: 11px;
 font-weight: 600;
 text-transform: uppercase;
}

.loading-state {
 display: flex;
 flex-direction: column;
 align-items: center;
 justify-content: center;
 padding: 60px 20px;
 color: #64748b;
}

.spinner {
 width: 40px;
 height: 40px;
 border: 4px solid #e2e8f0;
 border-top: 4px solid #667eea;
 border-radius: 50%;
 animation: spin 1s linear infinite;
 margin-bottom: 15px;
}

@keyframes spin {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
}

.empty-state {
 padding: 60px 20px;
 text-align: center;
 color: #64748b;
}

.empty-state p {
 font-size: 16px;
 margin: 0;
}

/* Filters */
.filters-section {
 display: flex;
 align-items: end;
 gap: 20px;
 padding: 25px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
 flex-wrap: wrap;
}

.filter-group {
 display: flex;
 flex-direction: column;
 gap: 5px;
}

.filter-group label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 text-transform: uppercase;
 letter-spacing: 0.5px;
}

.filter-select,
.filter-input {
 padding: 8px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 14px;
 min-width: 150px;
 transition: border-color 0.3s ease;
}

.filter-actions {
 display: flex;
 gap: 10px;
 align-items: end;
}

/* Pagination */
.pagination {
 display: flex;
 justify-content: center;
 align-items: center;
 gap: 20px;
 padding: 25px 30px;
 border-top: 1px solid #e2e8f0;
 background: #f8fafc;
}

.pagination-btn {
 background: #667eea;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
 background: #5a67d8;
}

.pagination-btn:disabled {
 background: #cbd5e0;
 cursor: not-allowed;
}

.pagination-info {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

/* Report Modal Styles */
.report-modal-overlay {
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background: rgba(0, 0, 0, 0.8);
 display: flex;
 align-items: center;
 justify-content: center;
 z-index: 10000;
 padding: 20px;
}

.report-modal {
 background: white;
 border-radius: 16px;
 width: 95vw;
 height: 90vh;
 max-width: 1600px;
 box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
 display: flex;
 flex-direction: column;
 overflow: hidden;
}

.modal-header {
 display: flex;
 justify-content: space-between;
 align-items: flex-start;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
 color: white;
}

.modal-title h2 {
 margin: 0 0 5px 0;
 font-size: 24px;
 font-weight: 700;
}

.modal-subtitle {
 font-size: 14px;
 display: flex;
 align-items: center;
 gap: 6px;
 opacity: 0.9;
}

.unsaved-indicator {
 color: #fbbf24;
 font-size: 12px;
 animation: pulse 2s infinite;
}

.saved-indicator {
 color: #10b981;
 font-size: 12px;
}

@keyframes pulse {
 0%, 100% { opacity: 1; }
 50% { opacity: 0.5; }
}

.close-btn {
 background: none;
 border: none;
 color: white;
 font-size: 30px;
 cursor: pointer;
 padding: 0;
 width: 40px;
 height: 40px;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 8px;
 transition: background 0.3s ease;
}

.close-btn:hover {
 background: rgba(255, 255, 255, 0.2);
}

.modal-content {
 display: flex;
 flex: 1;
 overflow: hidden;
}

.settings-panel {
 width: 400px;
 background: #f8fafc;
 border-right: 1px solid #e2e8f0;
 display: flex;
 flex-direction: column;
 overflow-y: auto;
}

.settings-header {
 padding: 25px 25px 20px;
 border-bottom: 1px solid #e2e8f0;
 background: white;
}

.settings-header h3 {
 margin: 0 0 5px 0;
 font-size: 18px;
 font-weight: 600;
 color: #1a202c;
}

.settings-header p {
 margin: 0;
 font-size: 14px;
 color: #64748b;
}

.settings-form {
 flex: 1;
 padding: 25px;
}

.setting-group {
 margin-bottom: 30px;
}

.subsection-title {
 font-size: 16px;
 font-weight: 600;
 color: #1a202c;
 margin: 0 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.form-row {
 margin-bottom: 20px;
}

.form-label {
 display: block;
 font-size: 14px;
 font-weight: 600;
 color: #374151;
 margin-bottom: 8px;
}

.input-group {
 position: relative;
 display: flex;
 align-items: center;
}

.input-prefix,
.input-suffix {
 position: absolute;
 color: #6b7280;
 font-weight: 500;
 z-index: 2;
 pointer-events: none;
 font-size: 13px;
}

.input-prefix {
 left: 12px;
}

.input-suffix {
 right: 12px;
}

.form-input {
 width: 100%;
 padding: 12px 16px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 font-size: 14px;
 font-weight: 500;
 transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-input:focus {
 outline: none;
 border-color: #667eea;
 box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-prefix + .form-input {
 padding-left: 35px;
}

.form-input + .input-suffix {
 right: 12px;
}

.radio-group {
 display: flex;
 flex-direction: column;
 gap: 10px;
 margin-top: 8px;
}

.radio-option {
 display: flex;
 align-items: center;
 gap: 8px;
 padding: 12px 14px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 cursor: pointer;
 transition: all 0.3s ease;
 font-size: 14px;
}

.radio-option:hover {
 border-color: #667eea;
 background: #f8fafc;
}

.radio-option input[type="radio"] {
 margin: 0;
}

.radio-option input[type="radio"]:checked + span {
 font-weight: 600;
 color: #667eea;
}

.cleaning-custom-rates,
.card-custom-rates {
 margin-top: 15px;
 padding: 15px;
 background: white;
 border-radius: 8px;
 border: 1px solid #e2e8f0;
}

.settings-actions {
 padding: 20px 25px;
 border-top: 1px solid #e2e8f0;
 background: white;
 display: flex;
 gap: 12px;
}

.settings-actions .btn {
 flex: 1;
 min-width: auto;
}

.preview-panel {
 flex: 1;
 display: flex;
 flex-direction: column;
 background: white;
 overflow: hidden;
}

.preview-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: white;
}

.preview-header h3 {
 margin: 0;
 font-size: 18px;
 font-weight: 600;
 color: #1a202c;
}

.preview-actions {
 display: flex;
 gap: 12px;
}

.preview-content {
 flex: 1;
 overflow-y: auto;
 padding: 30px;
 background: #f8f9fa;
}

/* Ajouter ces styles dans la section <style scoped> */

/* PDF Preview Styles - EXACTEMENT IDENTIQUES AU PDF */
.pdf-preview-container {
  font-family: 'Playfair Display', 'Times New Roman', serif;
  background: #f8f9f9;
  color: #2c3e50;
  font-size: 12px;
  line-height: 1.4;
}

.pdf-preview-container .page-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  background: white;
}

.pdf-preview-container .report-header {
  margin-bottom: 25px;
}

.pdf-preview-container .report-title {
  font-size: 38px;
  font-weight: 400;
  color: #2c3e50;
  margin-bottom: 5px;
  letter-spacing: 1px;
}

.pdf-preview-container .property-name {
  font-size: 22px;
  font-weight: 300;
  color: #34495e;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.pdf-preview-container .divider {
  width: 100%;
  height: 1px;
  background: #bdc3c7;
  margin: 15px 0 12px 0;
}

.pdf-preview-container .report-period {
  font-size: 13px;
  font-weight: 400;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.pdf-preview-container .section-title {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 20px 0 10px 0;
}

.pdf-preview-container .data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8px;
  margin-bottom: 15px;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .data-table th {
  background: #ecf0f1;
  padding: 5px 3px;
  text-align: center;
  font-weight: 600;
  border: 1px solid #bdc3c7;
  font-size: 7px;
  line-height: 1.2;
  color: #2c3e50;
}

.pdf-preview-container .data-table td {
  padding: 3px 2px;
  border: 1px solid #d5dbdb;
  font-size: 7px;
  vertical-align: middle;
  color: #2c3e50;
}

.pdf-preview-container .data-table .totals-row {
  background: #f8f9fa;
  font-weight: 700;
  border-top: 2px solid #34495e;
}

.pdf-preview-container .data-table .totals-row td {
  border: 1px solid #34495e;
  font-weight: 700;
  color: #2c3e50;
}

.pdf-preview-container .content-sections {
  margin: 15px 0;
}

.pdf-preview-container .summary-section {
  margin-bottom: 20px;
  margin-top: 20px;
}

.pdf-preview-container .summary-title {
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  color: #2c3e50;
  letter-spacing: 1px;
}

.pdf-preview-container .summary-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .summary-table td {
  padding: 2px 0;
  font-size: 8px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .summary-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .summary-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .summary-table .total-row {
  border-top: 1px solid #34495e;
  padding-top: 4px;
  margin-top: 4px;
  font-weight: 700;
  font-size: 11px;
}

.pdf-preview-container .summary-table .total-row td {
  padding-top: 4px;
  border-top: 1px solid #34495e;
}

.pdf-preview-container .settlement-section {
  margin: 25px 0;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  text-align: center;
}

.pdf-preview-container .settlement-title {
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #2c3e50;
}

.pdf-preview-container .settlement-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .settlement-table td {
  padding: 3px 0;
  font-size: 11px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .settlement-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .settlement-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .settlement-table .final-row {
  border-top: 2px solid #2c3e50;
  padding-top: 6px;
  margin-top: 6px;
  font-weight: 700;
  font-size: 13px;
}

.pdf-preview-container .settlement-table .final-row td {
  padding-top: 6px;
  border-top: 2px solid #2c3e50;
}

.pdf-preview-container .footer-section {
  margin-top: auto;
  padding-top: 15px;
  text-align: center;
  border-top: 1px solid #bdc3c7;
}

.pdf-preview-container .prepared-info {
  margin-bottom: 12px;
}

.pdf-preview-container .prepared-label {
  font-size: 8px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.pdf-preview-container .prepared-company {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
}

.pdf-preview-container .company-logo {
  margin-bottom: 15px;
}

.pdf-preview-container .company-name {
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 6px;
  color: #2c3e50;
  margin-bottom: 1px;
}

.pdf-preview-container .company-tagline {
  font-size: 6px;
  color: #95a5a6;
  text-transform: uppercase;
  letter-spacing: 3px;
}

.pdf-preview-container .footer-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  color: #7f8c8d;
  margin-top: 10px;
}



.report-preview .report-header {
 margin-bottom: 30px;
 text-align: center;
}

.report-preview .report-title {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0 0 10px 0;
 letter-spacing: 1px;
}

.report-preview .property-name {
 font-size: 24px;
 font-weight: 300;
 color: #34495e;
 margin: 0 0 15px 0;
 letter-spacing: 0.5px;
}

.report-preview .report-period {
 font-size: 14px;
 font-weight: 600;
 color: #7f8c8d;
 text-transform: uppercase;
 letter-spacing: 2px;
}

.report-preview .section-title {
 font-size: 14px;
 font-weight: 600;
 color: #2c3e50;
 text-transform: uppercase;
 letter-spacing: 1px;
 margin: 25px 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.report-preview .data-table {
 width: 100%;
 border-collapse: collapse;
 font-size: 12px;
 margin-bottom: 20px;
 font-family: Arial, sans-serif;
}

.report-preview .data-table th {
 background: #ecf0f1;
 padding: 10px 8px;
 text-align: center;
 font-weight: 600;
 border: 1px solid #bdc3c7;
 font-size: 11px;
 line-height: 1.2;
 color: #2c3e50;
}

.report-preview .data-table td {
 padding: 8px;
 border: 1px solid #d5dbdb;
 font-size: 11px;
 vertical-align: middle;
 color: #2c3e50;
}

.report-preview .totals-row {
 background: #f8f9fa;
 font-weight: 700;
 border-top: 2px solid #34495e;
}

.report-preview .totals-row td {
 border: 1px solid #34495e;
 font-weight: 700;
 color: #2c3e50;
}

.report-preview .summary-section {
 margin: 25px 0;
}

.report-preview .summary-title {
 font-size: 16px;
 font-weight: 700;
 margin-bottom: 15px;
 text-transform: uppercase;
 color: #2c3e50;
 letter-spacing: 1px;
}

.report-preview .summary-grid {
 display: flex;
 flex-direction: column;
 gap: 8px;
}

.report-preview .summary-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 8px 0;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .summary-item.total {
 border-top: 2px solid #34495e;
 padding-top: 12px;
 margin-top: 10px;
 font-weight: 700;
 font-size: 16px;
}

.report-preview .summary-label {
 font-weight: 500;
}

.report-preview .summary-value {
 font-weight: 600;
 color: #27ae60;
}

.report-preview .settlement-section {
 margin: 30px 0;
 padding: 20px;
 background: #f8f9fa;
 border: 1px solid #e9ecef;
 border-radius: 8px;
 text-align: center;
}

.report-preview .settlement-title {
 font-size: 18px;
 font-weight: 700;
 margin-bottom: 20px;
 text-transform: uppercase;
 letter-spacing: 1px;
 color: #2c3e50;
}

.report-preview .settlement-content {
 display: flex;
 flex-direction: column;
 gap: 12px;
}

.report-preview .settlement-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .settlement-item.final {
 border-top: 2px solid #2c3e50;
 padding-top: 15px;
 margin-top: 15px;
 font-weight: 700;
 font-size: 18px;
 color: #27ae60;
}

.report-preview .settlement-label {
 font-weight: 500;
}


.report-preview .settlement-value {
 font-weight: 600;
}

.report-preview .report-footer {
 margin-top: 40px;
 padding-top: 20px;
 border-top: 1px solid #bdc3c7;
 text-align: center;
}

.report-preview .prepared-info {
 margin-bottom: 15px;
}

.report-preview .prepared-label {
 font-size: 12px;
 color: #7f8c8d;
 margin-bottom: 2px;
}

.report-preview .prepared-company {
 font-size: 16px;
 font-weight: 600;
 color: #2c3e50;
}

.report-preview .generation-date {
 font-size: 12px;
 color: #95a5a6;
}

/* Responsive Design */
@media (max-width: 1200px) {
 .report-modal {
   width: 98vw;
   height: 95vh;
 }

 .modal-content {
   flex-direction: column;
 }

 .settings-panel {
   width: 100%;
   height: 50%;
   border-right: none;
   border-bottom: 1px solid #e2e8f0;
 }

 .preview-panel {
   height: 50%;
 }
}

@media (max-width: 768px) {
 .sidebar {
   transform: translateX(-100%);
 }
 
 .main-content {
   margin-left: 0;
   padding: 20px 15px;
 }
 
 .content-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .section-header {
   padding: 20px;
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .property-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
   padding: 15px 20px;
 }
 
 .property-stats {
   flex-direction: column;
   align-items: flex-start;
   gap: 8px;
 }
 
 .filters-section {
   flex-direction: column;
   align-items: stretch;
   gap: 15px;
   padding: 20px;
 }
 
 .filter-select,
 .filter-input {
   min-width: 100%;
 }
 
 .filter-actions {
   flex-direction: column;
   width: 100%;
 }
 
 .filter-actions .btn {
   width: 100%;
 }
 
 .data-table {
   font-size: 12px;
 }
 
 .data-table th,
 .data-table td {
   padding: 8px 6px;
 }
 
 .btn {
   width: 100%;
 }

 .report-modal {
   width: 100vw;
   height: 100vh;
   border-radius: 0;
   padding: 0;
 }

 .modal-header {
   padding: 15px 20px;
 }

 .modal-title h2 {
   font-size: 18px;
 }

 .settings-panel {
   height: 60%;
 }

 .preview-panel {
   height: 40%;
 }

 .settings-form {
   padding: 20px;
 }

 .preview-content {
   padding: 20px;
 }

 .report-preview {
   padding: 20px;
 }

 .section-title-container {
   gap: 8px;
 }
 
 .property-selector {
   flex-direction: column;
   align-items: flex-start;
   gap: 5px;
 }
 
 .property-select-small {
   width: 100%;
   min-width: 100%;
 }
}

/* Professional Email Modal Styles */
.email-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.email-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.email-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon .icon-circle {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.email-icon {
  font-size: 24px;
}

.header-text h2 {
  margin: 0 0 5px 0;
  font-size: 24px;
  font-weight: 600;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Body */
.email-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Report Preview Card */
.report-preview-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.card-content {
  padding: 25px;
}

.report-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.detail-icon {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 16px;
  color: #1a202c;
  font-weight: 600;
}

/* Recipient Section */
.recipient-section {
  margin-bottom: 25px;
}

.section-title {
  margin-bottom: 20px;
}

.section-title h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.section-title p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.recipient-selector {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.contact-dropdown {
  flex: 1;
}

.professional-select {
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.professional-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.manage-contacts-btn {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 15px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.manage-contacts-btn:hover {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  transform: translateY(-1px);
}

/* Selected Contact Preview */
.selected-contact-preview {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: #1a202c;
}

.contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.contact-info {
  flex: 1;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
}

.contact-email {
  font-size: 14px;
  opacity: 0.9;
}

.status-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

/* Email Options */
.email-options-section {
  margin-bottom: 25px;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.option-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.option-icon {
  font-size: 18px;
}

.option-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}

.option-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Custom Checkbox */
.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #4a5568;
  position: relative;
}

.checkbox-container input[type="checkbox"] {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

/* Message Textarea */
.message-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.message-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Footer */
.email-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.footer-info {
  display: flex;
  align-items: center;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #64748b;
}

.info-icon {
  font-size: 16px;
}

.footer-actions {
  display: flex;
  gap: 15px;
}

.cancel-btn {
  background: transparent;
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e0;
}

.send-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.send-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-icon {
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .email-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .header-content {
    gap: 15px;
  }
  
  .header-icon .icon-circle {
    width: 50px;
    height: 50px;
  }
  
  .email-modal-body {
    padding: 20px;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .recipient-selector {
    flex-direction: column;
  }
  
  .email-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .footer-actions {
    width: 100%;
    justify-content: center;
  }
}

/* Professional Contacts Management Modal Styles */
.contacts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.contacts-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

/* Header */
.contacts-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contacts-icon {
  font-size: 24px;
}

/* Body */
.contacts-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Add Contact Card */
.add-contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.add-contact-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.add-contact-card .card-header h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.add-contact-card .card-header p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.add-contact-form {
  padding: 25px;
}

.input-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.professional-input {
  padding: 15px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.professional-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.add-contact-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 15px 30px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  align-self: flex-start;
}

.add-contact-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.add-contact-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Contacts List Card */
.contacts-list-card {
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.contacts-list-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.header-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-with-count h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.contact-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Empty State */
.empty-contacts-state {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-contacts-state h4 {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

.empty-contacts-state p {
  margin: 0;
  font-size: 14px;
}

/* Contacts Grid */
.contacts-grid {
  padding: 25px;
  display: grid;
  gap: 15px;
}

.contact-item {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.contact-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.contact-item.editing {
  border-color: #f59e0b;
  background: #fffbeb;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.contact-item .contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.contact-details {
  flex: 1;
  min-width: 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.contact-email {
  font-size: 14px;
  color: #64748b;
  word-break: break-all;
}

.contact-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Edit Form */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edit-input {
  padding: 10px 15px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.edit-input:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

/* Contact Actions */
.contact-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;
}

.edit-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(59, 130, 246, 0.4);
}

.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.delete-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(239, 68, 68, 0.4);
}

.save-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(102, 126, 234, 0.4);
}

.cancel-btn {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

.cancel-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(107, 114, 128, 0.4);
}

/* Footer */
.contacts-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.close-modal-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.close-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* Responsive Design for Contacts Modal */
@media (max-width: 768px) {
  .contacts-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .contacts-modal-body {
    padding: 20px;
  }
  
  .input-group {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .contact-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .contact-details {
    width: 100%;
  }
  
  .contact-actions {
    align-self: flex-end;
  }
  
  .contacts-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}

/* Email Sent Popup Styles */
.email-sent-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.email-sent-popup {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: popupSlideIn 0.4s ease-out;
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: scale(0.7) translateY(-50px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-icon {
  margin-bottom: 25px;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #28a745;
  stroke-miterlimit: 10;
  margin: 0 auto 20px;
  box-shadow: inset 0px 0px 0px #28a745;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.success-checkmark .check-icon {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 50%;
  box-sizing: border-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon::before {
  top: 3px;
  left: -2px;
  width: 30px;
  transform-origin: 100% 50%;
  border-radius: 100px 0 0 100px;
}

.success-checkmark .check-icon::after {
  top: 0;
  left: 30px;
  width: 60px;
  transform-origin: 0 50%;
  border-radius: 0 100px 100px 0;
  animation: rotate-circle 4.25s ease-in;
}

.success-checkmark .check-icon::before,
.success-checkmark .check-icon::after {
  content: '';
  height: 100px;
  position: absolute;
  background: white;
  transform: rotate(-45deg);
}

.success-checkmark .check-icon .icon-line {
  height: 3px;
  background-color: #28a745;
  display: block;
  border-radius: 2px;
  position: absolute;
  z-index: 10;
}

.success-checkmark .check-icon .icon-line.line-tip {
  top: 46px;
  left: 14px;
  width: 25px;
  transform: rotate(45deg);
  animation: icon-line-tip 0.75s;
}

.success-checkmark .check-icon .icon-line.line-long {
  top: 38px;
  right: 8px;
  width: 47px;
  transform: rotate(-45deg);
  animation: icon-line-long 0.75s;
}

.success-checkmark .check-icon .icon-circle {
  top: -3px;
  left: -3px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  box-sizing: content-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon .icon-fix {
  top: 8px;
  width: 5px;
  left: 26px;
  z-index: 1;
  height: 85px;
  position: absolute;
  transform: rotate(-45deg);
  background-color: white;
}

@keyframes rotate-circle {
  0% {
    transform: rotate(-45deg);
  }
  5% {
    transform: rotate(-45deg);
  }
  12% {
    transform: rotate(-405deg);
  }
  100% {
    transform: rotate(-405deg);
  }
}

@keyframes icon-line-tip {
  0% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  54% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  70% {
    width: 50px;
    left: -8px;
    top: 37px;
  }
  84% {
    width: 17px;
    left: 21px;
    top: 48px;
  }
  100% {
    width: 25px;
    left: 14px;
    top: 45px;
  }
}

@keyframes icon-line-long {
  0% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  65% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  84% {
    width: 55px;
    right: 0px;
    top: 35px;
  }
  100% {
    width: 47px;
    right: 8px;
    top: 38px;
  }
}

.popup-content h2 {
  color: #28a745;
  margin-bottom: 15px;
  font-size: 28px;
  font-weight: 600;
}

.popup-content p {
  color: #555;
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 1.5;
}

.popup-content .delivery-info {
  color: #667eea;
  font-weight: 500;
  background: #f8faff;
  padding: 12px;
  border-radius: 8px;
  margin-top: 15px;
}

.popup-close-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.popup-close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.popup-close-btn:active {
  transform: translateY(0);
}

/* Checkbox styles for custom maintenance */
.checkbox-row {
  margin: 8px 0 12px 0;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.checkbox-option:hover {
  border-color: #667eea;
  background: #f1f5f9;
}

.checkbox-option input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
}

.checkbox-option input[type="checkbox"]:checked + span {
  font-weight: 600;
  color: #667eea;
}

.setting-description {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}
</style>